import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users, Building2, Package, ShoppingCart, Plus, Search, Tags,
  Trash2, Eye, EyeOff, LogOut, Bell, RefreshCw, Lock, KeyRound, X, AlertTriangle, Pencil, Check, Globe, Shield, ChevronRight,
  Phone, MapPin, Mail, User, ExternalLink, Archive, ArchiveRestore, Leaf, Send, ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import AdminSidebar from '../components/AdminSidebar';
import OrdersSubSidebar from '../components/OrdersSubSidebar';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from '../components/ui/sonner';
import { getCountryLabel, getCountryOptions } from '../countries';
import { invitationsApi } from '../api/invitationsApi';
import { apiClient } from '../lib/apiClient';
import { translateBlocker, blockersToBullets } from '../lib/blockerMessages';
import { PhoneInput } from '../components/PhoneInput';
import { checkPasswordRules } from '../lib/passwordPolicy';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleBadge = (role, zone) => {
  if (role === 'customer_admin') {
    return zone === 'green_zone' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700';
  }
  const colors = {
    ultra_admin:    'bg-slate-800 text-white',
    supplier_admin: 'bg-amber-100 text-amber-700',
    sub_user:       'bg-purple-100 text-purple-700',
  };
  return colors[role] || 'bg-gray-100 text-gray-600';
};

const roleLabel = (role, zone) => {
  if (role === 'customer_admin') {
    return zone === 'green_zone' ? 'Green Zone' : 'Blue Zone';
  }
  const labels = {
    ultra_admin:    'Ultra Admin',
    supplier_admin: 'Supplier Admin',
    sub_user:       'Sub User',
  };
  return labels[role] || role?.replace(/_/g, ' ');
};

const orderStatusBadge = (status) => {
  const colors = { confirmed: 'bg-green-100 text-green-700', pending: 'bg-yellow-100 text-yellow-700', cancelled: 'bg-red-100 text-red-700', completed: 'bg-blue-100 text-blue-700' };
  return colors[status] || 'bg-gray-100 text-gray-600';
};

const tenantTypeBadge = (type) => {
  const colors = { customer: 'bg-sky-100 text-sky-700', supplier: 'bg-orange-100 text-orange-700' };
  return colors[type] || 'bg-gray-100 text-gray-600';
};

// Phase 86AY.2 — Relative time formatter for "Davet edildi · 5 dk önce" badges.
// Bilingual. Returns empty string if iso is falsy/invalid.
const relativeTime = (iso, tr) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffSec = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60)   return tr ? `${diffSec} sn önce`  : `${diffSec}s ago`;
  const m = Math.floor(diffSec / 60);
  if (m < 60)         return tr ? `${m} dk önce`        : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)         return tr ? `${h} sa önce`        : `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30)         return tr ? `${d} gün önce`       : `${d}d ago`;
  return new Date(iso).toLocaleDateString(tr ? 'tr-TR' : 'en-US');
};

// Hiyerarşik Users View - tek kullanıcı satırı
const UserRow = ({ user, isMainUser, isSubUser, onView, onDelete, tr }) => {
  const isInactive = user.status === 'inactive';
  return (
  <div className={`flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors ${isSubUser ? 'pl-10' : ''} ${isInactive ? 'opacity-50' : ''}`}>
    {isSubUser && <ChevronRight className="h-3.5 w-3.5 text-gray-300 flex-shrink-0 -ml-2" />}
    <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold
      ${user.protected ? 'bg-amber-100 text-amber-700' : isInactive ? 'bg-gray-100 text-gray-400' : isSubUser ? 'bg-sky-100 text-sky-700' : isMainUser ? 'bg-purple-100 text-purple-700' : 'bg-purple-100 text-purple-700'}`}>
      {user.protected ? <Lock className="h-4 w-4" /> : (user.full_name || user.email || '?')[0].toUpperCase()}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`font-medium text-sm ${isInactive ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{user.full_name || '—'}</span>
        {user.protected && <Lock className="h-3 w-3 text-amber-500" />}
        <Badge className={`rounded-full text-xs ${roleBadge(user.role, user.zone)}`}>{roleLabel(user.role, user.zone)}</Badge>
        {isMainUser && user.role !== 'customer_admin' && <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 font-medium">{tr ? 'Ana Kullanıcı' : 'Main User'}</span>}
        {isSubUser && user.category_name && <span className="text-xs bg-sky-50 text-sky-600 rounded-full px-2 py-0.5">{user.category_name}</span>}
        {isInactive && <span className="text-xs bg-gray-100 text-gray-500 border border-gray-200 rounded-full px-2 py-0.5">{tr ? 'Pasif' : 'Inactive'}</span>}
      </div>
      <span className="text-xs text-gray-400">{user.email}</span>
    </div>
    <div className="flex items-center gap-1 ml-auto flex-shrink-0">
      <Button size="sm" variant="ghost" data-testid={`view-user-btn-${user.id}`} onClick={() => onView(user)}
        className="text-purple-600 hover:bg-purple-50 rounded-full h-8 w-8 p-0">
        <Eye className="h-4 w-4" />
      </Button>
      {user.protected ? (
        <span className="text-amber-400 flex items-center px-2"><Lock className="h-3.5 w-3.5" /></span>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => onDelete(user.id, user.full_name || user.email, 'users')}
          className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  </div>
  );
};

/* ── Ülke bayrağı haritası ───────────────────────────────────────────────── */
const COUNTRY_FLAGS = {
  // Türkçe ülke adları
  'türkiye': '🇹🇷', 'almanya': '🇩🇪', 'japonya': '🇯🇵', 'fransa': '🇫🇷', 'brezilya': '🇧🇷',
  'abd': '🇺🇸', 'ingiltere': '🇬🇧', 'çin': '🇨🇳', 'rusya': '🇷🇺', 'hindistan': '🇮🇳',
  'kanada': '🇨🇦', 'avustralya': '🇦🇺', 'italya': '🇮🇹', 'ispanya': '🇪🇸', 'meksika': '🇲🇽',
  'suudi arabistan': '🇸🇦', 'güney kore': '🇰🇷', 'endonezya': '🇮🇩', 'hollanda': '🇳🇱',
  'arjantin': '🇦🇷', 'portekiz': '🇵🇹', 'belçika': '🇧🇪', 'polonya': '🇵🇱', 'isveç': '🇸🇪',
  'norveç': '🇳🇴', 'danimarka': '🇩🇰', 'finlandiya': '🇫🇮', 'avusturya': '🇦🇹',
  'İsviçre': '🇨🇭', 'isviçre': '🇨🇭', 'yunanistan': '🇬🇷', 'mısır': '🇪🇬',
  'nijerya': '🇳🇬', 'güney afrika': '🇿🇦', 'kenya': '🇰🇪', 'pakistan': '🇵🇰',
  // İngilizce ülke adları
  'turkey': '🇹🇷', 'germany': '🇩🇪', 'japan': '🇯🇵', 'france': '🇫🇷', 'brazil': '🇧🇷',
  'usa': '🇺🇸', 'uk': '🇬🇧', 'china': '🇨🇳', 'russia': '🇷🇺', 'india': '🇮🇳',
  'canada': '🇨🇦', 'australia': '🇦🇺', 'italy': '🇮🇹', 'spain': '🇪🇸', 'mexico': '🇲🇽',
};

function getFlag(countryName) {
  if (!countryName) return null;
  return COUNTRY_FLAGS[countryName.toLowerCase()] || null;
}


const UserCard = ({ user, isMainUser, tenant, participant, onView, onDelete, onSendInvite, onResendSupplierInvite, onResendContractorInvite, tr }) => {
  const isInactive    = user.status === 'inactive';
  const isCustomer    = user.role === 'customer_admin';
  const isSupplier    = user.role === 'supplier_admin';
  const isGreenZone   = user.zone === 'green_zone' || tenant?.zone === 'green_zone';

  const ROLE_COLORS = {
    ultra_admin:      { bg: 'bg-slate-800',  text: 'text-white',  light: 'bg-slate-50',   label: 'Ultra Admin' },
    customer_admin:   isGreenZone
      ? { bg: 'bg-emerald-600', text: 'text-white', light: 'bg-emerald-50', label: tr ? 'Green Zone' : 'Green Zone' }
      : { bg: 'bg-blue-600',   text: 'text-white',  light: 'bg-blue-50',    label: tr ? 'Blue Zone'  : 'Blue Zone'  },
    supplier_admin:   { bg: 'bg-amber-500',  text: 'text-white',  light: 'bg-amber-50',   label: tr ? 'Tedarikçi'     : 'Supplier'   },
    contractor_admin: { bg: 'bg-orange-500', text: 'text-white',  light: 'bg-orange-50',  label: tr ? 'Yüklenici'     : 'Contractor' },
    sub_user:         { bg: 'bg-purple-500', text: 'text-white',  light: 'bg-purple-50',  label: tr ? 'Alt Kullanıcı' : 'Sub User'   },
  };
  const rc = ROLE_COLORS[user.role] || { bg: 'bg-gray-400', text: 'text-white', light: 'bg-gray-50', label: user.role };

  // Green Zone kartlarında stand kutusunun rengini yeşil yap
  const standColorClass = isGreenZone
    ? { wrap: 'bg-emerald-50 border-emerald-100', no: 'text-emerald-700', sub: 'text-emerald-500', area: 'text-emerald-800', sides: 'text-emerald-400' }
    : { wrap: 'bg-blue-50 border-blue-100',       no: 'text-blue-700',    sub: 'text-blue-500',    area: 'text-blue-800',    sides: 'text-blue-400'    };

  const isSubUser    = user.role === 'sub_user';

  const authPerson   = !isSubUser ? tenant?.authorized_person : null;
  const displayName  = authPerson?.name  || user.full_name || '—';
  const displayTitle = authPerson?.title || user.title     || '';
  const displayEmail = authPerson?.email || user.email     || '—';
  // Yetkili kişinin direkt hattı (sadece ana kullanıcılar için)
  const displayPhone = isSubUser ? '' : (authPerson?.phone || '');
  // Firma telefonu herkese gösterilir (sub user dahil)
  const companyPhone = tenant?.phone || '';
  const companyName  = tenant?.company_name || '';
  const country      = tenant?.country || user.country || '';
  const city         = tenant?.city || '';
  const address      = tenant?.address || '';
  const website      = tenant?.website || '';

  // initials: müşteri → firma adı, sub user → kendi adı, diğer → kullanıcı adı
  const initials = isCustomer
    ? (companyName || user.full_name || '?').slice(0, 2).toUpperCase()
    : (user.full_name || user.email || '?').slice(0, 2).toUpperCase();

  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col overflow-hidden ${isInactive ? 'opacity-60' : ''}`}>
      {/* Üst renkli şerit */}
      <div className={`h-1 ${rc.bg}`} />

      {/* Firma / Ülke başlık şeridi */}
      {(companyName || country) && (
        <div className={`px-4 pt-3 pb-2 ${rc.light} border-b border-gray-100`}>
          <div className="flex items-center gap-1.5">
            {isCustomer && !isGreenZone && getFlag(companyName || country)
              ? <span className="text-base leading-none select-none">{getFlag(companyName || country)}</span>
              : isCustomer
                ? <Globe className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                : <Building2 className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            }
            <p className="text-xs font-semibold text-gray-700 truncate leading-tight">
              {companyName || country}
            </p>
          </div>
          {(city || country) && (
            <p className="text-xs text-gray-400 mt-0.5 ml-5">
              {[city, country].filter(Boolean).join(', ')}
            </p>
          )}
        </div>
      )}

      <div className="p-4 flex flex-col gap-2.5 flex-1">
        {/* Avatar + Ad */}
        <div className="flex items-start gap-3">
          {/* Blue Zone: büyük bayrak emoji avatar; diğerleri: initials */}
          {isCustomer && !isGreenZone && getFlag(companyName || country) ? (
            <div className="h-12 w-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 bg-blue-50 border border-blue-100 select-none">
              {getFlag(companyName || country)}
            </div>
          ) : (
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 ${rc.bg} ${rc.text}`}>
              {user.protected ? <Lock className="h-4 w-4" /> : initials}
            </div>
          )}
          <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="font-semibold text-gray-900 text-sm leading-tight">{displayName}</p>
                {user.protected && <Lock className="h-3 w-3 text-amber-500 flex-shrink-0" />}
              </div>
              {displayTitle && <p className="text-xs text-gray-500 mt-0.5 leading-tight">{displayTitle}</p>}
              {isSupplier && user.category_name && (
                <p className="text-xs text-amber-600 font-medium mt-0.5">{user.category_name}</p>
              )}
            </div>
        </div>

        {/* Detay satırları */}
        <div className="space-y-1.5">
          {/* E-posta */}
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Mail className="h-3 w-3 text-gray-300 flex-shrink-0" />
            <span className="truncate">{displayEmail}</span>
          </div>

          {/* Telefon (kişisel / yetkili) */}
          {displayPhone && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Phone className="h-3 w-3 text-gray-300 flex-shrink-0" />
              <span>{displayPhone}</span>
            </div>
          )}

          {/* Şirket telefonu (farklıysa) */}
          {companyPhone && companyPhone !== displayPhone && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Building2 className="h-3 w-3 text-gray-300 flex-shrink-0" />
              <span>{companyPhone}</span>
            </div>
          )}

          {/* Adres */}
          {address && (
            <div className="flex items-start gap-2 text-xs text-gray-500">
              <MapPin className="h-3 w-3 text-gray-300 flex-shrink-0 mt-0.5" />
              <span className="line-clamp-2 leading-tight">{address}</span>
            </div>
          )}

          {/* Website */}
          {website && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <ExternalLink className="h-3 w-3 text-gray-300 flex-shrink-0" />
              <span className="truncate">{website}</span>
            </div>
          )}

          {/* Stand bilgisi — sadece müşteri (customer_admin) */}
          {isCustomer && participant?.stand_no && (
            <div className={`mt-1 rounded-xl px-3 py-2 ${standColorClass.wrap} border`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className={`text-xs font-bold ${standColorClass.no}`}>Stand {participant.stand_no}</p>
                  {participant.hall && <p className={`text-xs ${standColorClass.sub}`}>{participant.hall}</p>}
                </div>
                {participant.layout?.total_area > 0 && (
                  <span className={`text-sm font-bold flex-shrink-0 ${standColorClass.area}`}>
                    {participant.layout.total_area}m²
                  </span>
                )}
              </div>
              {participant.layout?.open_sides?.length > 0 && (
                <p className={`text-xs mt-1 ${standColorClass.sides}`}>
                  {tr ? 'Açık Kenarlar' : 'Open Sides'}: {participant.layout.open_sides.map(s =>
                    tr ? ({north:'Kuzey',south:'Güney',east:'Doğu',west:'Batı'}[s] || s) : s
                  ).join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Stand atanmamış müşteri uyarısı */}
          {isCustomer && !participant?.stand_no && (
            <div className="mt-1 rounded-xl px-3 py-1.5 bg-gray-50 border border-gray-100">
              <p className="text-xs text-gray-400 italic">{tr ? 'Stand henüz atanmadı' : 'No stand assigned'}</p>
            </div>
          )}
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 mt-auto pt-1">
          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${rc.bg} ${rc.text}`}>{rc.label}</span>
          {isMainUser && user.role !== 'ultra_admin' && (
            <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 font-medium">
              {tr ? 'Ana Kullanıcı' : 'Main User'}
            </span>
          )}
          {isInactive && (
            <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">
              {tr ? 'Pasif' : 'Inactive'}
            </span>
          )}
          {/* Phase 86AY.2 — Invitation-sent badge. Lets admin see at a glance
              that this user already received a welcome mail (and how long ago)
              without opening the modal. Hover title shows count. */}
          {participant?.last_invited_at && (
            <span
              data-testid={`invite-status-badge-${user.id}`}
              title={(tr ? 'Davet sayısı: ' : 'Invite count: ') + (participant?.invite_count || 1)}
              className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 font-medium inline-flex items-center gap-1">
              <Send className="w-2.5 h-2.5" />
              {tr ? 'Davet ·' : 'Invited ·'} {relativeTime(participant.last_invited_at, tr)}
              {(participant?.invite_count || 1) > 1 && (
                <span className="ml-1 bg-emerald-200 text-emerald-800 rounded-full px-1.5 text-[10px] font-bold">×{participant.invite_count}</span>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Aksiyon butonları */}
      <div className="border-t border-gray-50 px-3 py-2 flex items-center justify-between">
        {tenant?.slug && (
          <span className="text-xs text-gray-300 font-mono truncate max-w-[120px]">/{tenant.slug}</span>
        )}
        <div className="flex items-center gap-1 ml-auto">
          {/* Phase 86AY.1/2 — Green Zone müşterisine stand atandıysa
              "Davet Gönder" butonu (önizleme modalı açar). Daha önce davet
              edildiyse buton "Yeniden Davet Gönder" / hatırlatma olarak
              davranır (renk amber, ikon Bell). */}
          {user.role === 'customer_admin' && user.zone === 'green_zone' && participant?.stand_no && onSendInvite && (() => {
            const alreadyInvited = !!participant?.last_invited_at;
            return (
              <Button
                size="sm"
                variant="ghost"
                data-testid={`send-invite-btn-${user.id}`}
                onClick={() => onSendInvite(user, alreadyInvited)}
                title={alreadyInvited
                  ? (tr ? 'Yeniden Davet Gönder (Hatırlatma)' : 'Re-send Invitation (Reminder)')
                  : (tr ? 'Davet Maili Gönder' : 'Send Invitation Email')}
                className={`rounded-full h-7 w-7 p-0 ${alreadyInvited ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}`}>
                {alreadyInvited ? <Bell className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
              </Button>
            );
          })()}
          {/* Phase 86AZ.58 — Re-send 7-day setup link for manually-created
              suppliers who haven't activated yet (password_set=False). The
              button is hidden once the supplier sets their password. */}
          {user.role === 'supplier_admin' && user.password_set === false && onResendSupplierInvite && (() => {
            const alreadyInvited = !!(participant?.last_invited_at || user.last_invited_at);
            return (
              <Button
                size="sm"
                variant="ghost"
                data-testid={`resend-supplier-invite-btn-${user.id}`}
                onClick={() => onResendSupplierInvite(user)}
                title={alreadyInvited
                  ? (tr ? 'Şifre Belirleme Davetini Yeniden Gönder' : 'Re-send Password Setup Invite')
                  : (tr ? 'Şifre Belirleme Davetini Gönder' : 'Send Password Setup Invite')}
                className={`rounded-full h-7 w-7 p-0 ${alreadyInvited ? 'text-amber-600 hover:bg-amber-50' : 'text-amber-700 hover:bg-amber-50'}`}>
                {alreadyInvited ? <Bell className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
              </Button>
            );
          })()}
          {/* Phase 86AZ.59 — Same as above but for manually-created contractors. */}
          {user.role === 'contractor_admin' && user.password_set === false && onResendContractorInvite && (() => {
            const alreadyInvited = !!(participant?.last_invited_at || user.last_invited_at);
            return (
              <Button
                size="sm"
                variant="ghost"
                data-testid={`resend-contractor-invite-btn-${user.id}`}
                onClick={() => onResendContractorInvite(user)}
                title={alreadyInvited
                  ? (tr ? 'Şifre Belirleme Davetini Yeniden Gönder' : 'Re-send Password Setup Invite')
                  : (tr ? 'Şifre Belirleme Davetini Gönder' : 'Send Password Setup Invite')}
                className={`rounded-full h-7 w-7 p-0 ${alreadyInvited ? 'text-amber-600 hover:bg-amber-50' : 'text-red-700 hover:bg-red-50'}`}>
                {alreadyInvited ? <Bell className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
              </Button>
            );
          })()}
          <Button size="sm" variant="ghost" data-testid={`view-user-btn-${user.id}`} onClick={() => onView(user)}
            className="text-purple-600 hover:bg-purple-50 rounded-full h-7 w-7 p-0">
            <Eye className="h-3.5 w-3.5" />
          </Button>
          {user.protected ? (
            <span className="h-7 w-7 flex items-center justify-center text-amber-400"><Lock className="h-3.5 w-3.5" /></span>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => onDelete(user.id, user.full_name || user.email, 'users')}
              className="text-red-400 hover:bg-red-50 rounded-full h-7 w-7 p-0">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};


const UltraAdminSection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const tr = language === 'tr';

  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tenantZoneFilter, setTenantZoneFilter] = useState('all');
  const [tenantStatusFilter, setTenantStatusFilter] = useState('all');
  const [tenantCountryFilter, setTenantCountryFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState({});
  const [tenantsMap, setTenantsMap] = useState({});
  const [participantsByEmail, setParticipantsByEmail] = useState({});

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: '', entityType: '', hasPaidOrders: false });
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [ultraPassword, setUltraPassword] = useState('');
  const [ultraPwShow,   setUltraPwShow]   = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Phase 86U — Primary Super Admin force-delete (2-step) state.
  //   forceStep:    'idle' | 'arming' | 'confirm'
  //   forceCountdown: seconds remaining in arming phase (5 → 0)
  //   forcePassword2: second-pass password the SA must re-type to commit
  const [forceStep, setForceStep] = useState('idle');
  const [forceCountdown, setForceCountdown] = useState(0);
  const [forcePassword2, setForcePassword2] = useState('');
  const [forceError, setForceError] = useState('');

  // Current Ultra Admin email (read once from localStorage so we can gate the
  // override UI to mbucak@gmail.com).
  const currentAdminEmail = (() => {
    try { return (JSON.parse(localStorage.getItem('cop31_ultra_user') || '{}').email || '').toLowerCase(); }
    catch { return ''; }
  })();
  const isPrimarySuperAdmin = currentAdminEmail === 'mbucak@gmail.com';

  // Tick-down timer for the "arming" phase.
  useEffect(() => {
    if (forceStep !== 'arming') return undefined;
    if (forceCountdown <= 0) {
      setForceStep('confirm');
      return undefined;
    }
    const t = setTimeout(() => setForceCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [forceStep, forceCountdown]);

  // Tenant detail modal state
  const [viewTenant, setViewTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantUsersLoading, setTenantUsersLoading] = useState(false);

  const openTenantModal = async (tenant) => {
    setViewTenant(tenant);
    setTenantUsers([]);
    setTenantUsersLoading(true);
    try {
      const res = await apiClient.get(`/ultra-admin/users`);
      const users = (res.data || []).filter(u => u.tenant_id === tenant.id);
      setTenantUsers(users);
    } catch (_) {}
    setTenantUsersLoading(false);
  };

  // User detail modal state
  const [viewUser, setViewUser] = useState(null);
  const [pwdForm, setPwdForm] = useState({ new_password: '', confirm: '' });
  const [pwdShowNew,    setPwdShowNew]    = useState(false);
  const [pwdShowConfirm, setPwdShowConfirm] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [showPwdSection, setShowPwdSection] = useState(false);

  // Phase 86AT — Create-User modal (Ultra Admin sub-admins). The "+ New User"
  // button on the User Management page used to navigate to /ultra-admin/dashboard
  // which made no sense — admins reported "why does New User go to the dashboard?".
  // The modal now collects email + full name + password and calls
  // POST /ultra-admin/users to add another Ultra Admin (system admin).
  const [createUserModal, setCreateUserModal] = useState(null); // { email, full_name, password, confirm, loading, error }

  // Phase 86AT — Green Zone manual user creation modal. Fields mirror the
  // public /yesil-bolge registration form 1:1. Bypasses the application queue.
  const [createGzModal, setCreateGzModal] = useState(null);

  // Phase 86AZ.58 — Supplier manual creation modal. Mirrors /tedarikci-kayit
  // 1:1 (category, sub-category, company, full_name, title, phone, country,
  // city, email). NO password field — the supplier sets their own password
  // via a 7-day emailed invite link.
  const [createSupplierModal, setCreateSupplierModal] = useState(null);
  const [supplierCategories, setSupplierCategories] = useState([]);

  // Phase 86AZ.59 — Contractor (Müteahhit) manual creation modal. Mirrors
  // /yuklenci-kayit form fields. Same 7-day invite-link UX as supplier.
  const [createContractorModal, setCreateContractorModal] = useState(null);
  const [contractorCategories, setContractorCategories] = useState([]);

  // Phase 86AY.1 — Send Invitation modal for already-created Green Zone
  // customers with an assigned stand. Loads a server-rendered preview first,
  // admin reviews, hits "Gönder" → POST send-invite. No auto-mail anywhere.
  const [inviteModal, setInviteModal] = useState(null); // { user, preview, loading, sending, error }

  const openViewModal = (user) => {
    setViewUser(user);
    setPwdForm({ new_password: '', confirm: '' });
    setPwdError('');
    setPwdSuccess('');
    setShowPwdSection(false);
  };

  // Phase 86AT — submit handler for the Create-User modal. Validates the
  // password pair on the client and surfaces the backend detail message
  // (e.g. duplicate email) inside the modal instead of a generic toast.
  const handleCreateUser = async () => {
    const f = createUserModal || {};
    const email = (f.email || '').trim().toLowerCase();
    const full_name = (f.full_name || '').trim();
    const pw = f.password || '';
    const cf = f.confirm || '';
    if (!email || !full_name || !pw) {
      setCreateUserModal(prev => ({ ...prev, error: tr ? 'Tüm alanlar zorunlu.' : 'All fields are required.' }));
      return;
    }
    if (pw.length < 8) {
      setCreateUserModal(prev => ({ ...prev, error: tr ? 'Şifre en az 8 karakter olmalı.' : 'Password must be at least 8 characters.' }));
      return;
    }
    if (pw !== cf) {
      setCreateUserModal(prev => ({ ...prev, error: tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.' }));
      return;
    }
    setCreateUserModal(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const res = await apiClient.post(`/ultra-admin/users`, {
        email, full_name, password: pw, role: 'ultra_admin',
      });
      setData(prev => [res.data, ...prev]);
      setCreateUserModal(null);
      toast.success(tr ? 'Yeni Ultra Admin oluşturuldu.' : 'New Ultra Admin created.');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setCreateUserModal(prev => ({ ...prev, loading: false, error: typeof msg === 'string' ? msg : (tr ? 'Oluşturulamadı.' : 'Could not create.') }));
    }
  };

  // Phase 86AT — open the Green Zone modal pre-filled with empty fields.
  const openCreateGzModal = () => {
    setCreateGzModal({
      full_name: '', email: '', company: '', phone: '', phone_code: '+90',
      country_code: '', city: '', title: '', website: '', address: '',
      password: '', password2: '',
      loading: false, error: '', fieldErrors: {},
    });
  };

  // Phase 86AT — Submit handler for manual Green Zone creation.
  // Validations mirror the public form so admin can't bypass quality rules.
  const handleCreateGzUser = async () => {
    const f = createGzModal || {};
    const errs = {};
    const reqMsg = tr ? 'Bu alan zorunludur.' : 'This field is required.';
    if (!(f.full_name || '').trim()) errs.full_name = reqMsg;
    if (!(f.email || '').trim())     errs.email     = reqMsg;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) errs.email = tr ? 'Geçerli bir e-posta giriniz.' : 'Invalid email.';
    if (!(f.company || '').trim())   errs.company   = reqMsg;
    const rules = checkPasswordRules(f.password || '');
    const allRulesOk = Object.values(rules).every(Boolean);
    if (!f.password)              errs.password  = reqMsg;
    else if (!allRulesOk)         errs.password  = tr ? 'Şifre tüm kurallara uymalıdır.' : 'Password must meet all rules.';
    if (!f.password2)             errs.password2 = reqMsg;
    else if (f.password !== f.password2) errs.password2 = tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.';
    if (Object.keys(errs).length) {
      setCreateGzModal(prev => ({ ...prev, fieldErrors: errs, error: tr ? 'Lütfen işaretli alanları kontrol ediniz.' : 'Please review the highlighted fields.' }));
      return;
    }
    setCreateGzModal(prev => ({ ...prev, loading: true, error: '', fieldErrors: {} }));
    try {
      const res = await apiClient.post(`/ultra-admin/users/green-zone`, {
        full_name:    f.full_name.trim(),
        email:        f.email.trim().toLowerCase(),
        company:      f.company.trim(),
        phone:        f.phone ? `${f.phone_code} ${f.phone}` : null,
        country_code: f.country_code || null,
        city:         f.city || null,
        title:        f.title || null,
        website:      f.website || null,
        address:      f.address || null,
        password:     f.password,
        language:     language,
      });
      setData(prev => [res.data, ...prev]);
      setCreateGzModal(null);
      toast.success(tr ? 'Green Zone kullanıcısı oluşturuldu.' : 'Green Zone user created.');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setCreateGzModal(prev => ({ ...prev, loading: false, error: typeof msg === 'string' ? msg : (tr ? 'Oluşturulamadı.' : 'Could not create.') }));
    }
  };

  // Phase 86AZ.58 — Resend the 7-day supplier setup link. Generates a fresh
  // token + emails it. Only valid for password_set=False suppliers; the
  // button only renders on those cards.
  const handleResendSupplierInvite = async (user) => {
    if (!user?.id) return;
    try {
      const r = await apiClient.post(`/ultra-admin/users/${user.id}/supplier-resend-invite`);
      const data = r.data || {};
      const email = data.email || user.email;
      // Bump the per-user participant cache so the card flips to "Yeniden
      // gönderildi" badge without a full reload.
      setParticipantsByEmail(prev => ({
        ...prev,
        [email]: {
          ...(prev[email] || {}),
          last_invited_at: data.last_invited_at,
          invite_count: ((prev[email]?.invite_count) || 0) + 1,
        },
      }));
      toast.success(tr ? `Davet yeniden gönderildi: ${email}` : `Invitation re-sent: ${email}`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : (tr ? 'Davet gönderilemedi.' : 'Could not resend invitation.'));
    }
  };

  // ─── Phase 86AZ.58 — Supplier manual creation ─────────────────────────────
  // Open the modal pre-filled with empty fields and load supplier categories
  // (cached after first open to avoid an unnecessary round-trip).
  const openCreateSupplierModal = async () => {
    setCreateSupplierModal({
      full_name: '', email: '', company: '', title: '',
      phone: '', phone_code: '+90',
      country_code: '', city: '',
      supplier_category_id: '', supplier_category_name: '',
      supplier_sub_category_id: '', supplier_sub_category_name: '',
      loading: false, error: '', fieldErrors: {},
    });
    if (supplierCategories.length === 0) {
      try {
        const r = await apiClient.get('/auth/public/supplier-categories');
        setSupplierCategories(r.data || []);
      } catch {
        setSupplierCategories([{ id: 'default', name: 'Stand Constructor', subcategories: [] }]);
      }
    }
  };

  const handleCreateSupplierUser = async () => {
    const f = createSupplierModal || {};
    const errs = {};
    const reqMsg = tr ? 'Bu alan zorunludur.' : 'This field is required.';
    if (!(f.full_name || '').trim()) errs.full_name = reqMsg;
    if (!(f.email || '').trim()) errs.email = reqMsg;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) errs.email = tr ? 'Geçerli bir e-posta giriniz.' : 'Invalid email.';
    if (!(f.company || '').trim()) errs.company = reqMsg;
    if (!(f.supplier_category_id || '').trim()) errs.supplier_category_id = tr ? 'Tedarikçi kategorisi seçiniz.' : 'Please select a supplier category.';
    const activeCat = supplierCategories.find(c => c.id === f.supplier_category_id);
    const hasSubs = (activeCat?.subcategories || []).length > 0;
    if (hasSubs && !(f.supplier_sub_category_id || '').trim()) {
      errs.supplier_sub_category_id = tr ? 'Alt kategori seçiniz.' : 'Please select a sub-category.';
    }
    if (Object.keys(errs).length) {
      setCreateSupplierModal(prev => ({ ...prev, fieldErrors: errs, error: tr ? 'Lütfen işaretli alanları kontrol ediniz.' : 'Please review the highlighted fields.' }));
      return;
    }
    setCreateSupplierModal(prev => ({ ...prev, loading: true, error: '', fieldErrors: {} }));
    try {
      const res = await apiClient.post(`/ultra-admin/users/supplier`, {
        full_name:    f.full_name.trim(),
        email:        f.email.trim().toLowerCase(),
        company:      f.company.trim(),
        title:        f.title || null,
        phone:        f.phone ? `${f.phone_code} ${f.phone}` : null,
        country_code: f.country_code || null,
        city:         f.city || null,
        supplier_category_id:       f.supplier_category_id,
        supplier_category_name:     f.supplier_category_name,
        supplier_sub_category_id:   f.supplier_sub_category_id || '',
        supplier_sub_category_name: f.supplier_sub_category_name || '',
        language:     language,
      });
      setData(prev => [res.data, ...prev]);
      setCreateSupplierModal(null);
      const mailedMsg = res.data?.email_sent
        ? (tr ? 'Tedarikçi oluşturuldu. Davet e-postası gönderildi.' : 'Supplier created. Invitation email sent.')
        : (tr ? 'Tedarikçi oluşturuldu fakat e-posta gönderilemedi.' : 'Supplier created but email could not be sent.');
      toast.success(mailedMsg);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setCreateSupplierModal(prev => ({ ...prev, loading: false, error: typeof msg === 'string' ? msg : (tr ? 'Oluşturulamadı.' : 'Could not create.') }));
    }
  };

  // ─── Phase 86AZ.59 — Contractor (Müteahhit) manual creation ──────────────
  const openCreateContractorModal = async () => {
    setCreateContractorModal({
      full_name: '', email: '', company: '', title: '',
      phone: '', phone_code: '+90',
      country_code: '', city: '',
      website: '', about_us: '',
      contractor_category_id: '', contractor_category_name: '',
      loading: false, error: '', fieldErrors: {},
    });
    if (contractorCategories.length === 0) {
      try {
        const r = await apiClient.get('/auth/public/contractor-categories');
        setContractorCategories(r.data || []);
      } catch {
        setContractorCategories([{ id: 'default', name: 'Anahtar Teslim Stand Üretimi' }]);
      }
    }
  };

  const handleCreateContractorUser = async () => {
    const f = createContractorModal || {};
    const errs = {};
    const reqMsg = tr ? 'Bu alan zorunludur.' : 'This field is required.';
    if (!(f.full_name || '').trim()) errs.full_name = reqMsg;
    if (!(f.email || '').trim()) errs.email = reqMsg;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) errs.email = tr ? 'Geçerli bir e-posta giriniz.' : 'Invalid email.';
    if (!(f.company || '').trim()) errs.company = reqMsg;
    if (!(f.contractor_category_id || '').trim()) errs.contractor_category_id = tr ? 'Müteahhit kategorisi seçiniz.' : 'Please select a contractor category.';
    if (Object.keys(errs).length) {
      setCreateContractorModal(prev => ({ ...prev, fieldErrors: errs, error: tr ? 'Lütfen işaretli alanları kontrol ediniz.' : 'Please review the highlighted fields.' }));
      return;
    }
    setCreateContractorModal(prev => ({ ...prev, loading: true, error: '', fieldErrors: {} }));
    try {
      const res = await apiClient.post(`/ultra-admin/users/contractor`, {
        full_name:    f.full_name.trim(),
        email:        f.email.trim().toLowerCase(),
        company:      f.company.trim(),
        title:        f.title || null,
        phone:        f.phone ? `${f.phone_code} ${f.phone}` : null,
        country_code: f.country_code || null,
        city:         f.city || null,
        website:      f.website || null,
        about_us:     f.about_us || null,
        contractor_category_id:   f.contractor_category_id,
        contractor_category_name: f.contractor_category_name,
        language:     language,
      });
      setData(prev => [res.data, ...prev]);
      setCreateContractorModal(null);
      const mailedMsg = res.data?.email_sent
        ? (tr ? 'Müteahhit oluşturuldu. Davet e-postası gönderildi.' : 'Contractor created. Invitation email sent.')
        : (tr ? 'Müteahhit oluşturuldu fakat e-posta gönderilemedi.' : 'Contractor created but email could not be sent.');
      toast.success(mailedMsg);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setCreateContractorModal(prev => ({ ...prev, loading: false, error: typeof msg === 'string' ? msg : (tr ? 'Oluşturulamadı.' : 'Could not create.') }));
    }
  };

  const handleResendContractorInvite = async (user) => {
    if (!user?.id) return;
    try {
      const r = await apiClient.post(`/ultra-admin/users/${user.id}/contractor-resend-invite`);
      const data = r.data || {};
      const email = data.email || user.email;
      setParticipantsByEmail(prev => ({
        ...prev,
        [email]: {
          ...(prev[email] || {}),
          last_invited_at: data.last_invited_at,
          invite_count: ((prev[email]?.invite_count) || 0) + 1,
        },
      }));
      toast.success(tr ? `Davet yeniden gönderildi: ${email}` : `Invitation re-sent: ${email}`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : (tr ? 'Davet gönderilemedi.' : 'Could not resend invitation.'));
    }
  };

  // Phase 86AY.1/2 — Open the invite preview modal for a given user.
  // `isReminder` flips the modal copy to "Yeniden Davet" so admin sees the
  // distinction without losing the same preview-then-send flow.
  const openInviteModal = async (user, isReminder = false) => {
    setInviteModal({ user, isReminder, preview: null, loading: true, sending: false, error: '' });
    try {
      const res = await apiClient.post(`/ultra-admin/users/${user.id}/preview-invite`);
      setInviteModal({ user, isReminder, preview: res.data, loading: false, sending: false, error: '' });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setInviteModal({ user, isReminder, preview: null, loading: false, sending: false, error: typeof msg === 'string' ? msg : 'Error' });
    }
  };

  const handleSendInvite = async () => {
    const u = inviteModal?.user;
    if (!u) return;
    setInviteModal(prev => ({ ...prev, sending: true, error: '' }));
    try {
      const res = await apiClient.post(`/ultra-admin/users/${u.id}/send-invite`);
      // Phase 86AY.2 — Refresh participant cache so the new last_invited_at /
      // invite_count flow to the card badge immediately (no full reload).
      const email = res.data.email;
      if (email) {
        setParticipantsByEmail(prev => ({
          ...prev,
          [email]: {
            ...(prev[email] || {}),
            last_invited_at: res.data.last_invited_at,
            invite_count:    ((prev[email]?.invite_count) || 0) + 1,
          },
        }));
      }
      toast.success(inviteModal?.isReminder
        ? (tr ? `Hatırlatma maili gönderildi: ${email}` : `Reminder sent: ${email}`)
        : (tr ? `Davet maili gönderildi: ${email}`     : `Invitation sent: ${email}`));
      setInviteModal(null);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setInviteModal(prev => ({ ...prev, sending: false, error: typeof msg === 'string' ? msg : (tr ? 'Gönderilemedi.' : 'Could not send.') }));
    }
  };

  const handlePasswordChange = async () => {
    if (!pwdForm.new_password) { setPwdError(tr ? 'Yeni şifre boş olamaz.' : 'New password cannot be empty.'); return; }
    if (pwdForm.new_password !== pwdForm.confirm) { setPwdError(tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.'); return; }
    setPwdLoading(true);
    setPwdError('');
    setPwdSuccess('');
    try {
      await apiClient.put(
        `/ultra-admin/users/${viewUser.id}/password`,
        { new_password: pwdForm.new_password }
      );
      setPwdSuccess(tr ? 'Şifre başarıyla güncellendi.' : 'Password updated successfully.');
      setPwdForm({ new_password: '', confirm: '' });
      setShowPwdSection(false);
    } catch (err) {
      setPwdError(err.response?.data?.detail || (tr ? 'Şifre güncellenemedi.' : 'Could not update password.'));
    } finally {
      setPwdLoading(false);
    }
  };

  // Category management state
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [catForm, setCatForm] = useState({ name: '' });
  const [editingCat, setEditingCat] = useState(null); // { id, name }
  const [catError, setCatError] = useState('');

  // Determine section from path
  const path = location.pathname;
  const section = path.includes('/orders') ? 'orders'
    : path.includes('/user-categories') ? 'user-categories'
    : path.includes('/users') ? 'users'
    : path.includes('/products') ? 'products'
    : path.includes('/tenants') ? 'tenants'
    : 'users';

  const isOrderFilter = section === 'orders';

  const sectionConfig = {
    users: {
      title: tr ? 'Kullanıcı Yönetimi' : 'User Management',
      icon: Users,
      fetchUrl: `${API}/ultra-admin/users`,
      emptyText: tr ? 'Kullanıcı bulunamadı' : 'No users found'
    },
    tenants: {
      title: tr ? 'Tenant Yönetimi' : 'Tenant Management',
      icon: Building2,
      fetchUrl: `${API}/ultra-admin/tenants?page=1&limit=100&include_archived=true`,
      emptyText: tr ? 'Tenant bulunamadı' : 'No tenants found'
    },
    products: {
      title: tr ? 'Ürün Yönetimi' : 'Products Overview',
      icon: Package,
      fetchUrl: null,
      emptyText: tr ? 'Ürün bulunamadı' : 'No products found'
    },
    orders: {
      title: tr ? 'Genel Siparişler' : 'General Orders',
      icon: ShoppingCart,
      fetchUrl: `${API}/ultra-admin/all-orders`,
      emptyText: tr ? 'Sipariş bulunamadı' : 'No orders found'
    },
    'user-categories': {
      title: tr ? 'Kullanıcı Kategorileri' : 'User Categories',
      icon: Tags,
      fetchUrl: null, // handled separately
      emptyText: tr ? 'Kategori bulunamadı' : 'No categories found'
    }
  };

  const config = sectionConfig[section];

  useEffect(() => {
    setSearch('');
    setStatusFilter('all');
    setTenantZoneFilter('all');
    fetchData();
  }, [section]);

  useEffect(() => {
    applyFilter();
  }, [data, search, statusFilter, tenantZoneFilter, tenantStatusFilter, tenantCountryFilter]);

  const fetchCategories = async () => {
    setCatLoading(true);
    try {
      const cats = await invitationsApi.fetchAdminCategories();
      setCategories(cats);
    } catch (e) {
      setCatError('Kategoriler yüklenemedi.');
    } finally {
      setCatLoading(false);
    }
  };

  const handleAddCategory = async () => {
    const name = catForm.name.trim();
    if (!name) { setCatError('Kategori adı boş olamaz.'); return; }
    setCatError('');
    try {
      await invitationsApi.createCategory(name);
      setCatForm({ name: '' });
      fetchCategories();
    } catch (e) {
      setCatError(e.response?.data?.detail || 'Eklenemedi.');
    }
  };

  const handleUpdateCategory = async (id) => {
    if (!editingCat?.name.trim()) return;
    try {
      await invitationsApi.updateCategory(id, editingCat.name.trim());
      setEditingCat(null);
      fetchCategories();
    } catch (e) {
      setCatError(e.response?.data?.detail || (tr ? 'Güncellenemedi.' : 'Could not update.'));
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm(tr ? 'Bu kategoriyi silmek istediğinize emin misiniz?' : 'Are you sure you want to delete this category?')) return;
    try {
      await invitationsApi.deleteCategory(id);
      fetchCategories();
    } catch (e) {
      setCatError(e.response?.data?.detail || (tr ? 'Silinemedi.' : 'Could not delete.'));
    }
  };

  const fetchData = async () => {
    if (section === 'user-categories') { fetchCategories(); return; }
    setLoading(true);
    try {
      if (!config.fetchUrl) {
        setData([]);
        setLoading(false);
        return;
      }
      if (section === 'users') {
        // Fetch users + tenants + blue-zone-layouts in parallel
        const [usersRes, tenantsRes, layoutsRes] = await Promise.all([
          apiClient.get(`/ultra-admin/users?limit=500`),
          apiClient.get(`/ultra-admin/tenants?page=1&limit=100&include_archived=true`),
          apiClient.get(`/ultra-admin/blue-zone-layouts`).catch(() => ({ data: [] })),
        ]);
        const tenantsList = tenantsRes.data.tenants || tenantsRes.data || [];
        const map = {};
        tenantsList.forEach(t => { map[t.id] = t; });
        setTenantsMap(map);
        // participants map by email for customer stand info
        const pMap = {};
        (layoutsRes.data || []).forEach(p => { if (p.email) pMap[p.email] = p; });
        setParticipantsByEmail(pMap);
        setData(usersRes.data || []);
      } else {
        const res = await apiClient.get(config.fetchUrl);
        const result = section === 'tenants' ? (res.data.tenants || res.data) : res.data;
        setData(result || []);
      }
    } catch (err) {
      if (err.response?.status === 401) navigate('/giris?tab=ultra');
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    let result = [...data];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(item => {
        const fields = [
          item.email, item.full_name, item.company_name, item.name,
          item.slug, item.role, item.status, item.id, item.country
        ].filter(Boolean).map(f => f.toString().toLowerCase());
        return fields.some(f => f.includes(q));
      });
    }
    if (statusFilter !== 'all') {
      if (statusFilter === 'blue_zone') {
        result = result.filter(item => item.role === 'customer_admin' && item.zone !== 'green_zone');
      } else if (statusFilter === 'green_zone') {
        result = result.filter(item => item.role === 'customer_admin' && item.zone === 'green_zone');
      } else {
        result = result.filter(item => item.role === statusFilter);
      }
    }
    if (section === 'tenants' && tenantZoneFilter !== 'all') {
      result = result.filter(item => item.zone === tenantZoneFilter);
    }
    if (section === 'tenants' && tenantCountryFilter !== 'all') {
      result = result.filter(item => item.country_code === tenantCountryFilter);
    }
    if (section === 'tenants') {
      if (tenantStatusFilter === 'all') {
        // Default "Tümü" hides archived — user must click Arşivli tab to see them.
        result = result.filter(item => (item.status || 'active') !== 'archived');
      } else {
        result = result.filter(item => (item.status || 'active') === tenantStatusFilter);
      }
    }
    setFiltered(result);
  };

  const handleDeleteClick = async (id, name, entityType) => {
    setDeleteModal({ open: true, id, name, entityType, hasPaidOrders: false });
    setUltraPassword('');
    setDeleteError('');
    setDeletePreview(null);

    // Fetch the delete preview so we can warn Ultra Admin about blockers
    // (assigned stands, paid orders, protected users…) BEFORE they type the
    // password. Works for both `users` and `tenants` entity types — the
    // tenant preview returns `{ blockers, will_delete, safe_to_delete }`,
    // which we normalize into the same `tenant_cascade` shape used by the
    // user-delete modal so a single render branch handles both.
    if (entityType === 'users') {
      setDeletePreviewLoading(true);
      try {
        const res = await apiClient.get(`/ultra-admin/users/${id}/delete-preview`);
        setDeletePreview(res.data);
      } catch {
        // Preview is advisory — if it fails, fall back to legacy behavior.
      } finally {
        setDeletePreviewLoading(false);
      }
    } else if (entityType === 'tenants') {
      setDeletePreviewLoading(true);
      try {
        const res = await apiClient.get(`/ultra-admin/tenants/${id}/delete-preview`);
        // Normalise into the same shape the modal already renders for the
        // user-delete cascade so we don't duplicate UI logic.
        setDeletePreview({
          is_last_user: false,
          tenant_id: id,
          tenant_cascade: res.data,
        });
      } catch {
        // Preview is advisory — fall back silently.
      } finally {
        setDeletePreviewLoading(false);
      }
    }
  };

  const handleArchiveTenant = async (tenantId, tenantName) => {
    if (!window.confirm(tr
      ? `"${tenantName}" tenant'ı arşivlensin mi?\n\nArşivlenen tenant'ın kullanıcıları giriş yapamaz ama verileri korunur. İstediğiniz zaman arşivden çıkarabilirsiniz.`
      : `Archive tenant "${tenantName}"?\n\nUsers of an archived tenant cannot log in but all data is preserved. Can be un-archived anytime.`)) return;
    try {
      await apiClient.post(`/ultra-admin/tenants/${tenantId}/archive`, {});
      setData(prev => prev.map(t => t.id === tenantId ? { ...t, status: 'archived', previous_status: t.status } : t));
      toast.success(tr ? `'${tenantName}' arşivlendi.` : `'${tenantName}' archived.`);
    } catch (e) {
      toast.error(tr ? 'Arşivlenemedi: ' + (e.response?.data?.detail || e.message) : 'Archive failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleUnarchiveTenant = async (tenantId, tenantName) => {
    try {
      const res = await apiClient.post(`/ultra-admin/tenants/${tenantId}/unarchive`, {});
      setData(prev => prev.map(t => t.id === tenantId ? { ...t, status: res.data.new_status || 'active' } : t));
      toast.success(tr ? `'${tenantName}' arşivden çıkarıldı.` : `'${tenantName}' un-archived.`);
    } catch (e) {
      toast.error(tr ? 'Hata: ' + (e.response?.data?.detail || e.message) : 'Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDeleteConfirm = async (forceOverride = false, overridePassword = null) => {
    const passwordToUse = overridePassword !== null ? overridePassword : ultraPassword;
    if (!passwordToUse) {
      const setter = forceOverride ? setForceError : setDeleteError;
      setter(tr ? 'Ultra Password boş olamaz' : 'Ultra Password is required');
      return;
    }
    setDeleteLoading(true);
    setDeleteError('');
    setForceError('');
    try {
      const qs = `ultra_password=${encodeURIComponent(passwordToUse)}${forceOverride ? '&force_override=true' : ''}`;
      const resp = await apiClient.delete(
        `/ultra-admin/${deleteModal.entityType}/${deleteModal.id}?${qs}`
      );
      setData(prev => prev.filter(item => item.id !== deleteModal.id));
      setDeleteModal({ open: false, id: null, name: '', entityType: '', hasPaidOrders: false });
      setUltraPassword('');
      setForcePassword2('');
      setForceStep('idle');
      setForceCountdown(0);
      // Tenant cascade side-effects (only when entityType === 'users')
      const autoDeleted = resp.data?.tenant_auto_deleted;
      const autoBlocked = resp.data?.tenant_auto_blocked;
      const wasForced   = !!resp.data?.force_override;
      if (autoDeleted) {
        toast.success(tr
          ? `Kullanıcı silindi. Son kullanıcı olduğu için tenant da otomatik silindi.${wasForced ? ' (Force override)' : ''}`
          : `User deleted. As it was the last user, the tenant was also auto-deleted.${wasForced ? ' (Force override)' : ''}`);
      } else if (autoBlocked) {
        const lines = blockersToBullets(autoBlocked.blockers, tr);
        toast.warning(tr
          ? `Kullanıcı silindi, fakat tenant otomatik silinemedi:\n${lines}`
          : `User deleted, but tenant could not be auto-deleted:\n${lines}`);
      } else {
        toast.success(tr
          ? (deleteModal.entityType === 'tenants' ? `Tenant ve bağlı tüm veriler kalıcı olarak silindi.${wasForced ? ' (Force override)' : ''}` : `Kullanıcı kalıcı olarak silindi.${wasForced ? ' (Force override)' : ''}`)
          : (deleteModal.entityType === 'tenants' ? `Tenant and all associated data permanently deleted.${wasForced ? ' (Force override)' : ''}` : `User permanently deleted.${wasForced ? ' (Force override)' : ''}`));
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errSetter = forceOverride ? setForceError : setDeleteError;
      // 409 tenant → blocker list (active orders / assigned stands / paid tx)
      if (err.response?.status === 409 && deleteModal.entityType === 'tenants' && detail?.blockers) {
        const lines = blockersToBullets(detail.blockers, tr);
        errSetter((tr ? 'Tenant silinemez:\n' : 'Tenant cannot be deleted:\n') + lines);
      } else if (err.response?.status === 409 && detail?.code === 'last_user_blocked') {
        const lines = blockersToBullets(detail.blockers || [], tr);
        errSetter((tr ? 'Son kullanıcı silinemez:\n' : 'Last user cannot be deleted:\n') + lines);
      } else if (err.response?.status === 409 && detail?.code === 'has_paid_orders') {
        // 409 user → ödenmiş sipariş var, pasife alma seçeneği sun
        setDeleteModal(prev => ({ ...prev, hasPaidOrders: true }));
        errSetter('');
      } else {
        errSetter(typeof detail === 'string' ? detail : (detail?.message || (tr ? 'Hata oluştu' : 'Error occurred')));
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  // Primary Super Admin only: arm a 5-second countdown before showing the
  // second-password confirmation step.
  const handleArmForceDelete = () => {
    setForceError('');
    setForcePassword2('');
    setForceCountdown(5);
    setForceStep('arming');
  };
  const handleConfirmForceDelete = () => {
    handleDeleteConfirm(true, forcePassword2);
  };
  const handleCancelForceDelete = () => {
    setForceStep('idle');
    setForceCountdown(0);
    setForcePassword2('');
    setForceError('');
  };

  const handleDeactivateUser = async () => {
    setDeleteLoading(true);
    try {
      await apiClient.put(
        `/ultra-admin/users/${deleteModal.id}/deactivate`,
        {}
      );
      // Listede statusu güncelle
      setData(prev => prev.map(item => item.id === deleteModal.id ? { ...item, status: 'inactive' } : item));
      setDeleteModal({ open: false, id: null, name: '', entityType: '', hasPaidOrders: false });
      toast.success(tr ? 'Kullanıcı pasife alındı.' : 'User deactivated.');
    } catch (err) {
      setDeleteError(err.response?.data?.detail || (tr ? 'Pasife alma başarısız' : 'Deactivation failed'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('cop31_ultra_token');
    localStorage.removeItem('cop31_ultra_user');
    navigate('/giris?tab=ultra');
  };

  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      {section === 'orders' && <OrdersSubSidebar />}

      {/* ── Tenant Detail Modal ── */}
      {viewTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setViewTenant(null)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className={`p-6 ${viewTenant.zone === 'blue_zone' ? 'bg-blue-600' : viewTenant.zone === 'green_zone' ? 'bg-purple-700' : 'bg-gray-700'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-white/20 flex items-center justify-center">
                    <Globe className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">{viewTenant.company_name}</h2>
                    <p className="text-xs text-white/70">
                      {viewTenant.zone === 'blue_zone' ? 'Blue Zone' : viewTenant.zone === 'green_zone' ? 'Green Zone' : viewTenant.zone || '—'}
                    </p>
                  </div>
                </div>
                <button onClick={() => setViewTenant(null)} className="text-white/70 hover:text-white transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            {/* Body */}
            <div className="p-6 space-y-4">
              {/* Info grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: tr ? 'E-posta' : 'Email', value: viewTenant.email },
                  { label: tr ? 'Durum' : 'Status', value: viewTenant.status },
                  { label: tr ? 'Tür' : 'Type', value: viewTenant.company_type },
                  { label: tr ? 'Kayıt Tarihi' : 'Created', value: viewTenant.created_at ? new Date(viewTenant.created_at).toLocaleDateString() : '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                    <p className="text-sm font-medium text-gray-800 truncate">{value || '—'}</p>
                  </div>
                ))}
              </div>
              {/* Users */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {tr ? 'Kullanıcılar' : 'Users'} ({tenantUsers.length})
                </h3>
                {tenantUsersLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin h-5 w-5 border-2 border-emerald-500 border-t-transparent rounded-full" />
                  </div>
                ) : tenantUsers.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-3">{tr ? 'Kullanıcı bulunamadı' : 'No users found'}</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {tenantUsers.map(u => (
                      <div key={u.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2">
                        <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-purple-700">{(u.full_name || u.email || '?')[0].toUpperCase()}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">{u.full_name || '—'}</p>
                          <p className="text-xs text-gray-400 truncate">{u.email}</p>
                        </div>
                        <Badge className={`rounded-full text-xs px-2 py-0.5 ${roleBadge(u.role, u.zone)}`}>{roleLabel(u.role, u.zone)}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Footer — delete from modal */}
            <div className="px-6 pb-6 flex justify-between items-center border-t border-gray-100 pt-4">
              <div className="text-xs text-gray-400">
                {tr ? 'ID: ' : 'ID: '}{viewTenant?.id?.slice(0, 12)}...
              </div>
              {(viewTenant?.user_count ?? 0) > 0 ? (
                <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-xl px-3 py-2">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                  {tr ? `${viewTenant.user_count} kullanıcı var — önce kullanıcıları silin` : `${viewTenant.user_count} users — remove users first`}
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setViewTenant(null); handleDeleteClick(viewTenant.id, viewTenant.company_name, 'tenants'); }}
                  className="text-red-500 hover:bg-red-50 rounded-xl text-xs gap-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {tr ? 'Tenant\'ı Sil' : 'Delete Tenant'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Modal ── */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4">

            {deleteModal.hasPaidOrders ? (
              /* ── Paid-orders screen: cannot delete, offer deactivation ── */
              <>
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{tr ? 'Kullanıcı Silinemez' : 'Cannot Delete User'}</h3>
                    <p className="text-sm text-gray-500">{deleteModal.name}</p>
                  </div>
                  <button onClick={() => setDeleteModal({ open: false, id: null, name: '', entityType: '', hasPaidOrders: false })} className="ml-auto text-gray-400 hover:text-gray-600">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5">
                  <p className="text-sm text-amber-800 font-medium">
                    {tr
                      ? 'Bu kullanıcının ödenmiş/onaylanmış siparişleri bulunuyor. Sistem bütünlüğü için kalıcı olarak silinemez.'
                      : 'This user has paid/confirmed orders and cannot be permanently deleted for data integrity.'}
                  </p>
                  <p className="text-sm text-amber-700 mt-2">
                    {tr ? 'Bunun yerine kullanıcıyı pasife alabilirsiniz. Giriş yapamaz, ancak kayıtları korunur.' : 'You can deactivate them instead. They cannot login, but their records are preserved.'}
                  </p>
                </div>
                {deleteError && <p className="text-red-600 text-sm mb-3 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{deleteError}</p>}
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1 rounded-full" onClick={() => setDeleteModal({ open: false, id: null, name: '', entityType: '', hasPaidOrders: false })}>
                    {tr ? 'İptal' : 'Cancel'}
                  </Button>
                  <Button
                    data-testid="deactivate-user-btn"
                    onClick={handleDeactivateUser}
                    disabled={deleteLoading}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                  >
                    {deleteLoading
                      ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mx-auto" />
                      : <>{tr ? 'Pasife Al' : 'Deactivate'}</>}
                  </Button>
                </div>
              </>
            ) : (
              /* ── Normal delete screen ── */
              <>
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {deleteModal.entityType === 'tenants'
                        ? (tr ? 'Tenant\'ı Sil' : 'Delete Tenant')
                        : (tr ? 'Kullanıcıyı Sil' : 'Delete User')}
                    </h3>
                    <p className="text-sm text-gray-500">{deleteModal.name}</p>
                  </div>
                  <button onClick={() => setDeleteModal({ open: false, id: null, name: '', entityType: '', hasPaidOrders: false })} className="ml-auto text-gray-400 hover:text-gray-600">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="bg-red-50 rounded-2xl p-4 mb-5">
                  <p className="text-sm text-red-700">
                    {tr
                      ? 'Bu işlem geri alınamaz. Devam etmek için Ultra Password giriniz.'
                      : 'This action cannot be undone. Enter Ultra Password to continue.'}
                  </p>
                </div>

                {/* ─── Cascade warning: blockers / cascade impact ─── */}
                {deletePreviewLoading && (deleteModal.entityType === 'users' || deleteModal.entityType === 'tenants') && (
                  <div className="bg-gray-50 rounded-2xl p-3 mb-5 flex items-center gap-2 text-sm text-gray-500">
                    <div className="animate-spin h-3 w-3 border-2 border-gray-400 border-t-transparent rounded-full" />
                    {tr ? 'Silme etkisi hesaplanıyor...' : 'Computing delete impact...'}
                  </div>
                )}
                {deletePreview?.tenant_cascade && !deletePreview.tenant_cascade.error && (deletePreview.is_last_user || deleteModal.entityType === 'tenants') && (
                  <div className={`rounded-2xl p-4 mb-5 border-2 ${deletePreview.tenant_cascade.safe_to_delete ? 'bg-amber-50 border-amber-300' : 'bg-orange-50 border-orange-300'}`} data-testid="cascade-warning">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className={`h-5 w-5 ${deletePreview.tenant_cascade.safe_to_delete ? 'text-amber-600' : 'text-orange-600'} flex-shrink-0 mt-0.5`} />
                      <div className="flex-1">
                        <p className={`text-sm font-semibold ${deletePreview.tenant_cascade.safe_to_delete ? 'text-amber-800' : 'text-orange-800'}`}>
                          {(() => {
                            const isTenant = deleteModal.entityType === 'tenants';
                            const safe = deletePreview.tenant_cascade.safe_to_delete;
                            if (tr) {
                              if (isTenant) {
                                return safe
                                  ? 'Bu tenant ve bağlı tüm verileri silinecek'
                                  : 'Bu tenant silinemez — aşağıdaki kayıtlar engelliyor';
                              }
                              return safe
                                ? 'Bu son kullanıcıdır — silindiğinde tenant da otomatik silinecek'
                                : 'Bu kullanıcı silinemez — son kullanıcı ve tenant\'ta açık kayıtlar var';
                            }
                            if (isTenant) {
                              return safe
                                ? 'This tenant and all its data will be deleted'
                                : 'This tenant cannot be deleted — the records below are blocking it';
                            }
                            return safe
                              ? 'Last user — deleting will also auto-delete the tenant'
                              : 'This user cannot be deleted — last user with active records on the tenant';
                          })()}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {tr ? 'Tenant:' : 'Tenant:'} <span className="font-medium">{deletePreview.tenant_cascade.tenant?.company_name || deletePreview.tenant_id}</span>
                        </p>
                      </div>
                    </div>

                    {/* Safe-to-delete: show what will be removed */}
                    {deletePreview.tenant_cascade.safe_to_delete && (() => {
                      const wd = deletePreview.tenant_cascade.will_delete || {};
                      const entries = Object.entries(wd).filter(([, v]) => v > 0);
                      if (entries.length === 0) return (
                        <p className="text-xs text-amber-700 mt-2">
                          {tr ? 'Tenant bağlı ek veri olmadan silinecek.' : 'Tenant will be deleted with no attached data.'}
                        </p>
                      );
                      return (
                        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-amber-900">
                          {entries.map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between">
                              <span className="text-amber-700 capitalize">{k.replace(/_/g, ' ')}</span>
                              <span className="font-semibold">{v}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Has blockers: show them (user delete still proceeds, tenant will be left) */}
                    {!deletePreview.tenant_cascade.safe_to_delete && (deletePreview.tenant_cascade.blockers || []).length > 0 && (
                      <ul className="mt-2 space-y-1 text-xs text-orange-900">
                        {deletePreview.tenant_cascade.blockers.map((b, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <span className="text-orange-500">•</span>
                            <span>{translateBlocker(b, tr)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div className="space-y-2 mb-5">
                  <Label className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-gray-500" />
                    Ultra Password
                  </Label>
                  <div className="relative">
                    <Input
                      data-testid="ultra-password-input"
                      type={ultraPwShow ? 'text' : 'password'}
                      placeholder="••••••"
                      value={ultraPassword}
                      onChange={e => { setUltraPassword(e.target.value); setDeleteError(''); }}
                      onKeyDown={e => e.key === 'Enter' && handleDeleteConfirm()}
                      className={`rounded-xl text-center text-xl tracking-widest font-mono pr-10 ${deleteError ? 'border-red-400' : ''}`}
                      autoFocus
                    />
                    <button type="button" onClick={() => setUltraPwShow(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors">
                      {ultraPwShow ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {deleteError && (
                    <p className="text-red-600 text-sm flex items-center gap-1">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {deleteError}
                    </p>
                  )}
                </div>

                {(() => {
                  // Disable "Delete Permanently" when backend has reported
                  // hard blockers (e.g. assigned stand) — pressing it would
                  // just bounce with a 4xx error.
                  const isUserDelete   = deleteModal.entityType === 'users';
                  const isTenantDelete = deleteModal.entityType === 'tenants';
                  const hasBlockers    = !!(
                    deletePreview?.tenant_cascade &&
                    !deletePreview.tenant_cascade.error &&
                    deletePreview.tenant_cascade.safe_to_delete === false &&
                    (
                      // For users: only block when this is the last user
                      // (otherwise blockers belong to the tenant, not this user).
                      (isUserDelete && deletePreview?.is_last_user) ||
                      isTenantDelete
                    )
                  );
                  const blockerLabel = isTenantDelete
                    ? (tr
                        ? 'Bu tenant silinemez — önce yukarıdaki engelleri çöz'
                        : 'This tenant cannot be deleted — resolve the blockers above first')
                    : (tr
                        ? 'Bu kullanıcı silinemez — önce yukarıdaki engelleri çöz'
                        : 'This user cannot be deleted — resolve the blockers above first');
                  return (
                    <>
                      {hasBlockers && (
                        <p className="text-xs text-orange-700 mb-3 flex items-center gap-1.5" data-testid="delete-blocked-hint">
                          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                          {blockerLabel}
                        </p>
                      )}
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          className="flex-1 rounded-full"
                          onClick={() => { setDeleteModal({ open: false, id: null, name: '', entityType: '', hasPaidOrders: false }); setUltraPassword(''); handleCancelForceDelete(); }}
                        >
                          {tr ? 'İptal' : 'Cancel'}
                        </Button>
                        <Button
                          data-testid="confirm-delete-btn"
                          onClick={() => handleDeleteConfirm(false)}
                          disabled={deleteLoading || hasBlockers}
                          className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-full"
                          title={hasBlockers ? blockerLabel : undefined}
                        >
                          {deleteLoading
                            ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mx-auto" />
                            : <><Trash2 className="h-4 w-4 mr-2" />{tr ? 'Kalıcı Sil' : 'Delete Permanently'}</>}
                        </Button>
                      </div>

                      {/* Phase 86U — Primary Super Admin force-override path.
                          Only mbucak@gmail.com can bypass blockers, and only
                          via a 5s arming countdown + second password retype. */}
                      {hasBlockers && isPrimarySuperAdmin && forceStep === 'idle' && (
                        <div className="mt-4 pt-4 border-t border-dashed border-red-200">
                          <p className="text-[11px] text-gray-500 mb-2 flex items-center gap-1.5">
                            <Shield className="h-3 w-3 text-red-500" />
                            {tr
                              ? 'Primary Super Admin: Engelleyici kayıtları yok sayarak zorla sil.'
                              : 'Primary Super Admin: Bypass blockers and force delete.'}
                          </p>
                          <Button
                            data-testid="arm-force-delete-btn"
                            variant="outline"
                            onClick={handleArmForceDelete}
                            disabled={!ultraPassword || deleteLoading}
                            className="w-full rounded-full border-red-300 text-red-700 hover:bg-red-50"
                          >
                            <AlertTriangle className="h-4 w-4 mr-2" />
                            {tr ? 'Zorla Sil (Override)' : 'Force Delete (Override)'}
                          </Button>
                        </div>
                      )}

                      {hasBlockers && isPrimarySuperAdmin && forceStep === 'arming' && (
                        <div className="mt-4 rounded-2xl border-2 border-red-300 bg-red-50 p-4 text-center">
                          <p className="text-xs font-bold text-red-700 mb-1">
                            {tr ? 'Onay Hazırlanıyor' : 'Arming Confirmation'}
                          </p>
                          <p className="text-3xl font-mono font-bold text-red-600 mb-1" data-testid="force-countdown">
                            {forceCountdown}
                          </p>
                          <p className="text-[11px] text-red-700 mb-3">
                            {tr
                              ? 'İptal etmek için aşağıdaki düğmeye basın.'
                              : 'Press cancel below to abort.'}
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancelForceDelete}
                            className="rounded-full"
                          >
                            {tr ? 'İptal' : 'Cancel'}
                          </Button>
                        </div>
                      )}

                      {hasBlockers && isPrimarySuperAdmin && forceStep === 'confirm' && (
                        <div className="mt-4 rounded-2xl border-2 border-red-400 bg-red-50 p-4">
                          <p className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1.5">
                            <Shield className="h-4 w-4" />
                            {tr
                              ? 'Son Adım: Ultra Password\'ü tekrar girerek zorla silmeyi onaylayın'
                              : 'Final Step: Re-enter Ultra Password to confirm force delete'}
                          </p>
                          <div className="relative mb-2">
                            <Input
                              data-testid="force-password-input"
                              type="password"
                              placeholder="••••••"
                              value={forcePassword2}
                              onChange={e => { setForcePassword2(e.target.value); setForceError(''); }}
                              onKeyDown={e => e.key === 'Enter' && forcePassword2 && handleConfirmForceDelete()}
                              className="rounded-xl text-center text-lg tracking-widest font-mono"
                              autoFocus
                            />
                          </div>
                          {forceError && (
                            <p className="text-red-600 text-xs flex items-center gap-1 mb-2">
                              <AlertTriangle className="h-3 w-3" />
                              {forceError}
                            </p>
                          )}
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCancelForceDelete}
                              className="flex-1 rounded-full"
                            >
                              {tr ? 'İptal' : 'Cancel'}
                            </Button>
                            <Button
                              data-testid="confirm-force-delete-btn"
                              onClick={handleConfirmForceDelete}
                              disabled={!forcePassword2 || deleteLoading}
                              size="sm"
                              className="flex-1 bg-red-700 hover:bg-red-800 text-white rounded-full disabled:bg-gray-300"
                            >
                              {deleteLoading
                                ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mx-auto" />
                                : <><AlertTriangle className="h-3.5 w-3.5 mr-1" />{tr ? 'Zorla Sil' : 'Force Delete'}</>}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── User Detail Modal ── */}
      {viewUser && (() => {
        const vTenant = tenantsMap[viewUser.tenant_id];
        const isCustomer = viewUser.role === 'customer_admin';
        const isSupplier = viewUser.role === 'supplier_admin';
        const isSubUser  = viewUser.role === 'sub_user';
        const vAuthPerson = !isSubUser ? vTenant?.authorized_person : null;
        const vParticipant = participantsByEmail[viewUser.email];
        const isGreenZone = viewUser.zone === 'green_zone' || vTenant?.zone === 'green_zone';
        const headerGrad = viewUser.protected
          ? 'bg-gradient-to-r from-amber-500 to-orange-400'
          : viewUser.role === 'contractor_admin'
            ? 'bg-gradient-to-r from-orange-500 to-amber-400'
            : viewUser.role === 'supplier_admin'
              ? 'bg-gradient-to-r from-amber-500 to-yellow-400'
              : isGreenZone
                ? 'bg-gradient-to-r from-emerald-600 to-teal-500'
                : 'bg-gradient-to-r from-blue-600 to-blue-500';

        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className={`px-6 py-5 flex-shrink-0 ${headerGrad}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {viewUser.protected
                      ? <Lock className="h-6 w-6 text-white" />
                      : (isCustomer && !isGreenZone && getFlag(vTenant?.company_name || viewUser.country))
                        ? <span className="text-3xl leading-none select-none">{getFlag(vTenant?.company_name || viewUser.country)}</span>
                        : <span className="text-white font-bold text-xl">{(viewUser.full_name || viewUser.email || '?').charAt(0).toUpperCase()}</span>
                    }
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      {isCustomer && !isGreenZone && getFlag(vTenant?.company_name || viewUser.country) && (
                        <span className="text-lg leading-none">{getFlag(vTenant?.company_name || viewUser.country)}</span>
                      )}
                      <h2 className="text-lg font-bold text-white leading-tight">{vTenant?.company_name || viewUser.full_name || '—'}</h2>
                    </div>
                    {isCustomer && vTenant?.city && (
                      <p className="text-xs text-white/80 mt-0.5 leading-tight">{vTenant.city}, {vTenant.country}</p>
                    )}
                    {!isCustomer && vTenant?.company_name && (
                      <p className="text-xs text-white/80 mt-0.5 leading-tight">{vTenant.company_name}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs bg-white/20 text-white rounded-full px-2.5 py-0.5 font-medium">
                        {roleLabel(viewUser.role, viewUser.protected)}
                      </span>
                      {viewUser.protected && (
                        <span className="text-xs bg-white/20 text-white rounded-full px-2.5 py-0.5 flex items-center gap-1">
                          <Lock className="h-3 w-3" />
                          {tr ? 'Korumalı' : 'Protected'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button onClick={() => setViewUser(null)} className="text-white/70 hover:text-white transition-colors flex-shrink-0">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Body — scrollable */}
            <div className="overflow-y-auto flex-1">
              {/* Firma / Tenant Bilgileri — sadece tenant varsa göster */}
              {vTenant && (
                <div className="px-6 pt-5 pb-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    {tr ? 'Firma Bilgileri' : 'Company Details'}
                  </p>
                  <div className="space-y-2.5">
                    {vTenant.company_name && (
                      <div className="flex items-start gap-2.5">
                        <Building2 className="h-4 w-4 text-gray-300 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-800 font-medium">{vTenant.company_name}</span>
                      </div>
                    )}
                    {(vTenant.city || vTenant.country) && (
                      <div className="flex items-center gap-2.5">
                        <Globe className="h-4 w-4 text-gray-300 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{[vTenant.city, vTenant.country].filter(Boolean).join(', ')}</span>
                      </div>
                    )}
                    {vTenant.phone && (
                      <div className="flex items-center gap-2.5">
                        <Phone className="h-4 w-4 text-gray-300 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{vTenant.phone}</span>
                        <span className="text-xs text-gray-400">{tr ? '(Merkez)' : '(Main)'}</span>
                      </div>
                    )}
                    {vTenant.address && (
                      <div className="flex items-start gap-2.5">
                        <MapPin className="h-4 w-4 text-gray-300 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-600 leading-snug">{vTenant.address}</span>
                      </div>
                    )}
                    {vTenant.website && (
                      <div className="flex items-center gap-2.5">
                        <ExternalLink className="h-4 w-4 text-gray-300 flex-shrink-0" />
                        <span className="text-sm text-blue-500">{vTenant.website}</span>
                      </div>
                    )}
                    {vTenant.tax_no && (
                      <div className="flex items-center gap-2.5">
                        <span className="text-xs text-gray-400 ml-6">VKN: {vTenant.tax_no} / {vTenant.tax_office}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Yetkili Kişi — sadece authorized_person varsa */}
              {vAuthPerson && (
                <div className="px-6 pt-3 pb-3 border-t border-gray-50 mt-1">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    {tr ? 'Yetkili Kişi' : 'Authorized Person'}
                  </p>
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2.5">
                      <User className="h-4 w-4 text-gray-300 flex-shrink-0" />
                      <span className="text-sm text-gray-800 font-medium">{vAuthPerson.name}</span>
                      {vAuthPerson.title && <span className="text-xs text-gray-400">— {vAuthPerson.title}</span>}
                    </div>
                    {vAuthPerson.email && (
                      <div className="flex items-center gap-2.5">
                        <Mail className="h-4 w-4 text-gray-300 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{vAuthPerson.email}</span>
                      </div>
                    )}
                    {vAuthPerson.phone && (
                      <div className="flex items-center gap-2.5">
                        <Phone className="h-4 w-4 text-gray-300 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{vAuthPerson.phone}</span>
                        <span className="text-xs text-gray-400">{tr ? '(Direkt)' : '(Direct)'}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Stand Bilgisi — Müşteri (customer_admin) */}
              {isCustomer && vParticipant?.stand_no && (
                <div className="px-6 pt-3 pb-3 border-t border-gray-50 mt-1">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    {tr ? 'Stand Bilgisi' : 'Stand Assignment'}
                  </p>
                  <div className={`rounded-2xl p-4 space-y-2 ${isGreenZone ? 'bg-emerald-50' : 'bg-blue-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold uppercase tracking-wider ${isGreenZone ? 'text-emerald-600' : 'text-blue-600'}`}>Stand No</span>
                      <span className={`text-lg font-bold ${isGreenZone ? 'text-emerald-800' : 'text-blue-800'}`}>{vParticipant.stand_no}</span>
                    </div>
                    {vParticipant.hall && (
                      <div className="flex items-center justify-between">
                        <span className={`text-xs ${isGreenZone ? 'text-emerald-500' : 'text-blue-500'}`}>{tr ? 'Salon' : 'Hall'}</span>
                        <span className={`text-sm font-medium ${isGreenZone ? 'text-emerald-700' : 'text-blue-700'}`}>{vParticipant.hall}</span>
                      </div>
                    )}
                    {vParticipant.layout?.total_area > 0 && (
                      <div className="flex items-center justify-between">
                        <span className={`text-xs ${isGreenZone ? 'text-emerald-500' : 'text-blue-500'}`}>{tr ? 'Alan' : 'Area'}</span>
                        <span className={`text-sm font-bold ${isGreenZone ? 'text-emerald-700' : 'text-blue-700'}`}>{vParticipant.layout.total_area} m²</span>
                      </div>
                    )}
                    {vParticipant.layout?.open_sides?.length > 0 && (
                      <div className="flex items-center justify-between">
                        <span className={`text-xs ${isGreenZone ? 'text-emerald-500' : 'text-blue-500'}`}>{tr ? 'Açık Kenarlar' : 'Open Sides'}</span>
                        <span className={`text-xs font-medium ${isGreenZone ? 'text-emerald-600' : 'text-blue-600'}`}>
                          {vParticipant.layout.open_sides.map(s =>
                            tr ? ({north:'Kuzey',south:'Güney',east:'Doğu',west:'Batı'}[s] || s) : s
                          ).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Stand atanmamış uyarısı */}
              {isCustomer && !vParticipant?.stand_no && (
                <div className="px-6 pt-3 pb-3 border-t border-gray-50 mt-1">
                  <div className="bg-gray-50 rounded-2xl px-4 py-3 text-sm text-gray-400 italic text-center">
                    {tr ? 'Stand henüz atanmamış' : 'No stand assigned yet'}
                  </div>
                </div>
              )}

              {/* Kategori — Tedarikçi (supplier_admin) */}
              {isSupplier && viewUser.category_name && (
                <div className="px-6 pt-3 pb-3 border-t border-gray-50 mt-1">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    {tr ? 'Hizmet Kategorisi' : 'Service Category'}
                  </p>
                  <div className="flex items-center gap-3 bg-amber-50 rounded-2xl px-4 py-3">
                    <Tags className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <span className="text-sm font-semibold text-amber-800">{viewUser.category_name}</span>
                  </div>
                </div>
              )}

              {/* Hesap Bilgileri */}
              <div className="px-6 pt-3 pb-4 border-t border-gray-50 mt-1">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  {tr ? 'Hesap Bilgileri' : 'Account Info'}
                </p>
                <div className="space-y-0">
                  {[
                    { label: tr ? 'Giriş E-postası' : 'Login Email', value: viewUser.email, icon: '✉️' },
                    ...(!vTenant ? [{ label: tr ? 'Ülke' : 'Country', value: viewUser.country || '—', icon: '🌍' }] : []),
                    { label: tr ? 'Durum' : 'Status', value: viewUser.status || 'active', icon: '✅' },
                    { label: tr ? 'Kayıt Tarihi' : 'Created At', value: viewUser.created_at ? new Date(viewUser.created_at).toLocaleString(tr ? 'tr-TR' : 'en-US') : '—', icon: '📅' },
                    { label: tr ? 'Son Giriş' : 'Last Login', value: viewUser.last_login ? new Date(viewUser.last_login).toLocaleString(tr ? 'tr-TR' : 'en-US') : (tr ? 'Henüz giriş yapılmadı' : 'Never'), icon: '🔑' },
                    { label: tr ? 'Kullanıcı ID' : 'User ID', value: viewUser.id ? `${viewUser.id.slice(0, 8)}...${viewUser.id.slice(-4)}` : '—', icon: '🆔', mono: true },
                    { label: tr ? 'Tenant ID' : 'Tenant ID', value: viewUser.tenant_id ? `${viewUser.tenant_id.slice(0, 8)}...` : '—', icon: '🏢', mono: true },
                  ].map(({ label, value, icon, mono }) => (
                    <div key={label} className="flex items-start justify-between py-2 border-b border-gray-50 last:border-0">
                      <span className="text-sm text-gray-500 flex items-center gap-2 flex-shrink-0">
                        <span>{icon}</span>
                        {label}
                      </span>
                      <span className={`text-sm font-medium text-gray-800 text-right ml-4 ${mono ? 'font-mono text-xs text-gray-500' : ''}`}>
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Password Section */}
            <div className="px-6 pb-2 flex-shrink-0 border-t border-gray-50">
              <button
                data-testid="toggle-pwd-section"
                onClick={() => { setShowPwdSection(!showPwdSection); setPwdError(''); setPwdSuccess(''); setPwdForm({ new_password: '', confirm: '' }); }}
                className="flex items-center gap-2 text-sm font-medium text-purple-700 hover:text-purple-700 w-full py-2"
              >
                <KeyRound className="h-4 w-4" />
                {showPwdSection ? (tr ? 'Şifre bölümünü kapat' : 'Close password section') : (tr ? 'Şifreyi Değiştir' : 'Change Password')}
              </button>

              {showPwdSection && (
                <div className="space-y-3 pb-4">
                  <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-2.5 text-xs text-amber-700">
                    {tr
                      ? 'Şifreler güvenli hash ile saklanır, mevcut şifre görüntülenemez. Aşağıdan yeni şifre belirleyebilirsiniz.'
                      : 'Passwords are stored as a secure hash; the current password cannot be shown. You can set a new password below.'}
                  </div>
                  {pwdSuccess && (
                    <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-2.5 text-xs text-purple-700 font-medium">
                      ✓ {pwdSuccess}
                    </div>
                  )}
                  {pwdError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 text-xs text-red-600">
                      {pwdError}
                    </div>
                  )}
                  <div className="space-y-2">
                    <div className="relative">
                      <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        data-testid="new-password-input"
                        type={pwdShowNew ? 'text' : 'password'}
                        placeholder={tr ? 'Yeni şifre' : 'New password'}
                        value={pwdForm.new_password}
                        onChange={(e) => { setPwdForm(p => ({ ...p, new_password: e.target.value })); setPwdError(''); }}
                        className="w-full pl-9 pr-10 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <button type="button" onClick={() => setPwdShowNew(s => !s)}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-700 transition-colors">
                        {pwdShowNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        data-testid="confirm-password-input"
                        type={pwdShowConfirm ? 'text' : 'password'}
                        placeholder={tr ? 'Şifre tekrar' : 'Confirm password'}
                        value={pwdForm.confirm}
                        onChange={(e) => { setPwdForm(p => ({ ...p, confirm: e.target.value })); setPwdError(''); }}
                        className={`w-full pl-9 pr-10 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 ${pwdForm.confirm && pwdForm.new_password !== pwdForm.confirm ? 'border-red-400' : 'border-gray-200'}`}
                      />
                      <button type="button" onClick={() => setPwdShowConfirm(s => !s)}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-700 transition-colors">
                        {pwdShowConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {pwdForm.confirm && pwdForm.new_password !== pwdForm.confirm && (
                      <p className="text-red-500 text-xs font-medium">
                        {tr ? 'Şifreler eşleşmiyor!' : 'Passwords do not match!'}
                      </p>
                    )}
                  </div>
                  <Button
                    data-testid="save-password-btn"
                    onClick={handlePasswordChange}
                    disabled={pwdLoading}
                    className="w-full bg-purple-700 hover:bg-purple-800 text-white rounded-full py-2 text-sm"
                  >
                    <KeyRound className="h-4 w-4 mr-2" />
                    {pwdLoading ? (tr ? 'Kaydediliyor...' : 'Saving...') : (tr ? 'Şifreyi Kaydet' : 'Save Password')}
                  </Button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 flex justify-end gap-3 border-t border-gray-50 pt-4 flex-shrink-0">
              <Button
                variant="outline"
                className="rounded-full px-6"
                onClick={() => setViewUser(null)}
              >
                {tr ? 'Kapat' : 'Close'}
              </Button>
              {!viewUser.protected && (
                <Button
                  onClick={() => { setViewUser(null); handleDeleteClick(viewUser.id, viewUser.full_name || viewUser.email, 'users'); }}
                  className="rounded-full px-6 bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {tr ? 'Sil' : 'Delete'}
                </Button>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      <div className="flex-1 overflow-hidden">
        {/* Topbar */}
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="px-6">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-purple-100 flex items-center justify-center">
                  <Icon className="h-5 w-5 text-purple-700" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900">{config.title}</h1>
                  <p className="text-xs text-gray-500">COP 31 {tr ? 'Platform Yönetimi' : 'Platform Management'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" className="rounded-full" onClick={fetchData}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" className="rounded-full"><Bell className="h-5 w-5" /></Button>
                <LanguageToggle variant="admin" />
                <Button onClick={handleLogout} variant="outline" size="sm" className="rounded-full">
                  <LogOut className="h-4 w-4 mr-2" />
                  {tr ? 'Çıkış' : 'Logout'}
                </Button>
              </div>
            </div>
          </div>
        </nav>

        {/* Content */}
        <div className="p-6 max-w-7xl">

          {/* ── User Categories Section ── */}
          {section === 'user-categories' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{tr ? 'Kullanıcı Kategorileri' : 'User Categories'}</h2>
                <p className="text-sm text-gray-500">{tr ? 'Davet edilebilecek kullanıcı kategorilerini yönetin' : 'Manage user categories for invitations'}</p>
              </div>

              {/* Add category form */}
              <Card className="border-0 shadow-sm rounded-2xl">
                <CardContent className="pt-5 pb-5">
                  <div className="flex gap-3">
                    <Input
                      data-testid="cat-name-input"
                      placeholder={tr ? 'Kategori adı...' : 'Category name...'}
                      value={catForm.name}
                      onChange={(e) => { setCatForm({ name: e.target.value }); setCatError(''); }}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                      className="rounded-xl flex-1"
                    />
                    <Button
                      data-testid="cat-add-btn"
                      onClick={handleAddCategory}
                      className="bg-purple-700 hover:bg-purple-800 text-white rounded-xl gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      {tr ? 'Ekle' : 'Add'}
                    </Button>
                  </div>
                  {catError && <p className="text-red-500 text-xs mt-2">{catError}</p>}
                </CardContent>
              </Card>

              {/* Category list */}
              <Card className="border-0 shadow-sm rounded-2xl">
                <CardContent className="pt-4 pb-4">
                  {catLoading ? (
                    <div className="text-center py-8"><div className="animate-spin h-8 w-8 border-4 border-purple-700 border-t-transparent rounded-full mx-auto" /></div>
                  ) : categories.length === 0 ? (
                    <div className="text-center py-10 text-gray-400 text-sm">{tr ? 'Kategori bulunamadı.' : 'No categories found.'}</div>
                  ) : (
                    <div className="space-y-2">
                      {categories.map((cat) => (
                        <div key={cat.id} className="flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                          {editingCat?.id === cat.id ? (
                            <div className="flex-1 flex gap-2 mr-2">
                              <Input
                                data-testid={`cat-edit-input-${cat.id}`}
                                value={editingCat.name}
                                onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })}
                                className="h-8 rounded-lg text-sm flex-1"
                                onKeyDown={(e) => e.key === 'Enter' && handleUpdateCategory(cat.id)}
                                autoFocus
                              />
                              <Button size="sm" onClick={() => handleUpdateCategory(cat.id)} className="bg-purple-700 text-white rounded-lg h-8">
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingCat(null)} className="rounded-lg h-8">
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <span className="font-medium text-gray-800 text-sm flex-1">{cat.name}</span>
                          )}
                          {editingCat?.id !== cat.id && (
                            <div className="flex gap-1">
                              <Button
                                data-testid={`cat-edit-btn-${cat.id}`}
                                size="sm" variant="ghost"
                                onClick={() => setEditingCat({ id: cat.id, name: cat.name })}
                                className="text-blue-500 hover:bg-blue-50 rounded-lg"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                data-testid={`cat-delete-btn-${cat.id}`}
                                size="sm" variant="ghost"
                                onClick={() => handleDeleteCategory(cat.id)}
                                className="text-red-500 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── Standard Sections (users, tenants, products, orders) ── */}
          {section !== 'user-categories' && (<>
          {/* Header + Actions */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{config?.title}</h2>
              <p className="text-sm text-gray-500">
                {filtered.length} {tr ? 'kayıt' : 'records'}
              </p>
            </div>
            <div className="flex gap-3">
              {section === 'users' && (
                <>
                  <Button
                    data-testid="new-gz-user-btn"
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-full"
                    onClick={openCreateGzModal}>
                    <Leaf className="h-4 w-4 mr-2" />
                    {tr ? 'Yeni Green Zone' : 'New Green Zone'}
                  </Button>
                  <Button
                    data-testid="new-supplier-user-btn"
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 rounded-full"
                    onClick={openCreateSupplierModal}>
                    <Package className="h-4 w-4 mr-2" />
                    {tr ? 'Yeni Tedarikçi' : 'New Supplier'}
                  </Button>
                  <Button
                    data-testid="new-contractor-user-btn"
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50 rounded-full"
                    onClick={openCreateContractorModal}>
                    <Plus className="h-4 w-4 mr-2" />
                    {tr ? 'Yeni Müteahhit' : 'New Contractor'}
                  </Button>
                  <Button
                    data-testid="new-user-btn"
                    className="bg-purple-700 hover:bg-purple-800 text-white rounded-full"
                    onClick={() => setCreateUserModal({ email: '', full_name: '', password: '', confirm: '', loading: false, error: '' })}>
                    <Plus className="h-4 w-4 mr-2" />
                    {tr ? 'Yeni Kullanıcı' : 'New User'}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Filters */}
          <Card className="border-0 shadow-sm rounded-2xl mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    data-testid="section-search"
                    placeholder={tr ? 'Ara...' : 'Search...'}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9 rounded-xl text-sm"
                  />
                </div>
                {isOrderFilter && (
                  <div className="flex gap-2">
                    {['all', 'confirmed', 'pending', 'cancelled', 'completed'].map(s => (
                      <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${statusFilter === s ? 'bg-purple-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      >
                        {s === 'all' ? (tr ? 'Tümü' : 'All') : s}
                      </button>
                    ))}
                  </div>
                )}
                {section === 'users' && (
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { key: 'all',              label: tr ? 'Tümü'        : 'All'            },
                      { key: 'ultra_admin',       label: 'Ultra Admin'                         },
                      { key: 'blue_zone',         label: 'Blue Zone'                           },
                      { key: 'green_zone',        label: 'Green Zone'                          },
                      { key: 'supplier_admin',    label: tr ? 'Tedarikçi'   : 'Supplier'        },
                      { key: 'contractor_admin',  label: tr ? 'Yüklenici'   : 'Contractor'      },
                      { key: 'sub_user',          label: tr ? 'Alt Kullanıcı': 'Sub User'       },
                    ].map(({ key, label }) => {
                      // Phase 86AZ.59 — Pending-activation badge on supplier/contractor
                      // chips so the admin sees at a glance how many invited users
                      // haven't set their password yet (manual onboarding queue).
                      const pendingCount = key === 'supplier_admin'
                        ? data.filter(u => u.role === 'supplier_admin' && u.password_set === false).length
                        : key === 'contractor_admin'
                        ? data.filter(u => u.role === 'contractor_admin' && u.password_set === false).length
                        : 0;
                      return (
                      <button
                        key={key}
                        onClick={() => setStatusFilter(key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          statusFilter === key
                            ? key === 'blue_zone'  ? 'bg-blue-600 text-white'
                            : key === 'green_zone' ? 'bg-emerald-600 text-white'
                            : 'bg-purple-700 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {label}
                        {pendingCount > 0 && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                            statusFilter === key ? 'bg-white/20' : 'bg-amber-100 text-amber-700'
                          }`} title={tr ? 'Aktivasyon bekleyen' : 'Pending activation'}>
                            {pendingCount}
                          </span>
                        )}
                      </button>
                      );
                    })}
                  </div>
                )}
                {section === 'tenants' && (
                  <div className="flex flex-col gap-3">
                    {/* Durum tabları */}
                    <div className="flex gap-2 flex-wrap">
                      {[
                        { key: 'all',      label: tr ? 'Tümü'      : 'All',      color: 'bg-gray-700'    },
                        { key: 'active',   label: tr ? 'Aktif'     : 'Active',   color: 'bg-emerald-600' },
                        { key: 'pending',  label: tr ? 'Beklemede' : 'Pending',  color: 'bg-amber-500'   },
                        { key: 'inactive', label: tr ? 'Pasif'     : 'Inactive', color: 'bg-red-500'     },
                        { key: 'archived', label: tr ? 'Arşivli'   : 'Archived', color: 'bg-zinc-600'    },
                      ].map(({ key, label, color }) => {
                        const count = key === 'all'
                          ? data.filter(t => (t.status || 'active') !== 'archived').length
                          : data.filter(t => (t.status || 'active') === key).length;
                        return (
                          <button
                            key={key}
                            onClick={() => setTenantStatusFilter(key)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              tenantStatusFilter === key ? `${color} text-white` : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {label}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                              tenantStatusFilter === key ? 'bg-white/20' : 'bg-gray-200 text-gray-500'
                            }`}>{count}</span>
                          </button>
                        );
                      })}
                    </div>
                    {/* Zone tabları */}
                    <div className="flex gap-2 flex-wrap">
                      {[
                        { key: 'all',        label: tr ? 'Tüm Zonlar' : 'All Zones',  color: 'bg-gray-600'     },
                        { key: 'blue_zone',  label: 'Blue Zone',                       color: 'bg-blue-600'     },
                        { key: 'green_zone', label: 'Green Zone',                      color: 'bg-emerald-600'  },
                        { key: 'supplier',   label: tr ? 'Tedarikçi' : 'Supplier',     color: 'bg-amber-500'    },
                        { key: 'contractor', label: tr ? 'Yüklenici'  : 'Contractor',  color: 'bg-orange-500'   },
                      ].map(({ key, label, color }) => (
                        <button
                          key={key}
                          onClick={() => setTenantZoneFilter(key)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${tenantZoneFilter === key ? `${color} text-white` : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >
                          {label}
                          {key !== 'all' && (
                            <span className="ml-1.5 opacity-75">
                              {data.filter(t => t.zone === key).length}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                    {/* Country filter — sadece DB'de country_code'u olan tenant'lardan türetilir */}
                    {(() => {
                      const codeCounts = data.reduce((acc, t) => {
                        if (t.country_code) acc[t.country_code] = (acc[t.country_code] || 0) + 1;
                        return acc;
                      }, {});
                      const codes = Object.keys(codeCounts).sort((a, b) =>
                        getCountryLabel(a, language).localeCompare(
                          getCountryLabel(b, language), tr ? 'tr' : 'en'
                        )
                      );
                      if (codes.length === 0) return null;
                      return (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mr-1">
                            {tr ? 'Ülke' : 'Country'}
                          </span>
                          <button
                            onClick={() => setTenantCountryFilter('all')}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${tenantCountryFilter === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            data-testid="country-filter-all"
                          >
                            {tr ? 'Tümü' : 'All'}
                            <span className="ml-1.5 opacity-75">{codes.reduce((s, c) => s + codeCounts[c], 0)}</span>
                          </button>
                          <select
                            value={tenantCountryFilter}
                            onChange={e => setTenantCountryFilter(e.target.value)}
                            className="border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium bg-white focus:outline-none focus:ring-2 focus:ring-purple-300 cursor-pointer"
                            data-testid="country-filter-select"
                          >
                            <option value="all">{tr ? '— Ülke seç —' : '— Select country —'}</option>
                            {codes.map(code => (
                              <option key={code} value={code}>
                                {getCountryLabel(code, language)} ({codeCounts[code]})
                              </option>
                            ))}
                          </select>
                          {tenantCountryFilter !== 'all' && (
                            <button
                              onClick={() => setTenantCountryFilter('all')}
                              className="text-xs text-purple-600 hover:text-purple-800 underline"
                            >
                              {tr ? 'Filtreyi temizle' : 'Clear filter'}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Users — Hiyerarşik Görünüm */}
          {section === 'users' && (
            loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Users className="h-14 w-14 mx-auto mb-4 text-gray-200" />
                <p>{tr ? 'Kullanıcı bulunamadı' : 'No users found'}</p>
              </div>
            ) : (() => {
              // Build hierarchy
              const sysAdmins = filtered.filter(u => u.role === 'ultra_admin' && !u.tenant_id);
              const groups = {};
              filtered.filter(u => !(u.role === 'ultra_admin' && !u.tenant_id)).forEach(u => {
                const tid = u.tenant_id || '__no_tenant__';
                if (!groups[tid]) groups[tid] = [];
                groups[tid].push(u);
              });
              // ultra_admins with tenant → add to their group too
              filtered.filter(u => u.role === 'ultra_admin' && u.tenant_id).forEach(u => {
                const tid = u.tenant_id;
                if (!groups[tid]) groups[tid] = [];
                if (!groups[tid].find(x => x.id === u.id)) groups[tid].push(u);
              });

              return (
                <div className="p-4">
                  {/* Sistem Yöneticileri grid */}
                  {sysAdmins.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Shield className="h-4 w-4 text-purple-600" />
                        <span className="font-semibold text-purple-900 text-sm">{tr ? 'Sistem Yöneticileri' : 'System Admins'}</span>
                        <span className="text-xs bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 font-medium">{sysAdmins.length}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {sysAdmins.map(u => (
                          <UserCard key={u.id} user={u} onView={openViewModal} onDelete={handleDeleteClick} tr={tr} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tenant grupları — grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {Object.entries(groups).map(([tenantId, users]) => {
                      const tenant = tenantsMap[tenantId];
                      const mainUser = users.find(u => u.role !== 'sub_user') || users[0];
                      return users.map(u => (
                        <UserCard
                          key={u.id}
                          user={u}
                          isMainUser={u.id === mainUser?.id}
                          tenant={tenant}
                          participant={participantsByEmail[u.email]}
                          onView={openViewModal}
                          onDelete={handleDeleteClick}
                          onSendInvite={openInviteModal}
                          onResendSupplierInvite={handleResendSupplierInvite}
                          onResendContractorInvite={handleResendContractorInvite}
                          tr={tr}
                        />
                      ));
                    })}
                  </div>
                </div>
              );
            })()
          )}

          {/* Data Table — Tenants / Orders / Products */}
          {section !== 'users' && (
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-16">
                    <Icon className="h-14 w-14 mx-auto mb-4 text-gray-200" />
                    <p className="text-gray-400">{config.emptyText}</p>
                    {section === 'products' && (
                      <p className="text-sm text-gray-400 mt-2">
                        {tr ? "Ürün kataloğu client-side'da (mockAdmin.js)" : 'Product catalog is client-side (mockAdmin.js)'}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-100 bg-gray-50">
                          {/* Tenants columns */}
                          {section === 'tenants' && (
                            <>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Tenant / Şirket' : 'Tenant / Company'}</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Zone</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Mail' : 'Mail'}</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Kullanıcılar' : 'Users'}</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Durum' : 'Status'}</th>
                              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'İşlemler' : 'Actions'}</th>
                            </>
                          )}
                          {/* Orders columns */}
                          {section === 'orders' && (
                            <>
                              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Sipariş ID' : 'Order ID'}</th>
                              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Durum' : 'Status'}</th>
                              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Ürünler' : 'Items'}</th>
                              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Toplam' : 'Total'}</th>
                              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Tarih' : 'Date'}</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {filtered.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                            {/* Tenants row */}
                            {section === 'tenants' && (
                              <>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                                      item.zone === 'blue_zone'   ? 'bg-blue-100'    :
                                      item.zone === 'green_zone'  ? 'bg-emerald-100' :
                                      item.zone === 'supplier'    ? 'bg-amber-100'   :
                                      item.zone === 'contractor'  ? 'bg-orange-100'  : 'bg-gray-100'
                                    }`}>
                                      <Globe className={`h-4 w-4 ${
                                        item.zone === 'blue_zone'   ? 'text-blue-600'    :
                                        item.zone === 'green_zone'  ? 'text-emerald-600' :
                                        item.zone === 'supplier'    ? 'text-amber-600'   :
                                        item.zone === 'contractor'  ? 'text-orange-600'  : 'text-gray-500'
                                      }`} />
                                    </div>
                                    <span className="font-medium text-gray-900 text-xs">{item.company_name || item.name || '—'}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                  {item.zone === 'blue_zone'   && <Badge className="rounded-full text-xs bg-blue-100 text-blue-700 border border-blue-200 px-2 py-0.5">Blue Zone</Badge>}
                                  {item.zone === 'green_zone'  && <Badge className="rounded-full text-xs bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5">Green Zone</Badge>}
                                  {item.zone === 'supplier'    && <Badge className="rounded-full text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5">{tr ? 'Tedarikçi' : 'Supplier'}</Badge>}
                                  {item.zone === 'contractor'  && <Badge className="rounded-full text-xs bg-orange-100 text-orange-700 border border-orange-200 px-2 py-0.5">{tr ? 'Yüklenici' : 'Contractor'}</Badge>}
                                  {!item.zone && <Badge className="rounded-full text-xs bg-gray-100 text-gray-500 px-2 py-0.5">—</Badge>}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500">{item.email || '—'}</td>
                                <td className="px-4 py-3">
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-700">
                                    <Users className="h-3.5 w-3.5 text-gray-400" />
                                    {item.user_count ?? 0}
                                    <span className="text-gray-400">{tr ? 'kişi' : 'users'}</span>
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <Badge className={`rounded-full text-xs px-2 py-0.5 ${
                                    item.status === 'active'   ? 'bg-green-100 text-green-700'
                                  : item.status === 'pending'  ? 'bg-amber-100 text-amber-700'
                                  : item.status === 'archived' ? 'bg-zinc-100 text-zinc-700 border border-zinc-200'
                                  :                              'bg-gray-100 text-gray-600'
                                  }`}>{item.status === 'archived' ? (tr ? 'arşivli' : 'archived') : item.status}</Badge>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <Button size="sm" variant="ghost" onClick={() => openTenantModal(item)} className="text-blue-500 hover:bg-blue-50 rounded-full h-7 w-7 p-0" title={tr ? 'Detay' : 'View'} data-testid={`tenant-view-btn-${item.id}`}>
                                      <Eye className="h-3.5 w-3.5" />
                                    </Button>
                                    {item.id !== 'summitflow-platform' && (
                                      <>
                                        {item.status === 'archived' ? (
                                          <Button size="sm" variant="ghost" onClick={() => handleUnarchiveTenant(item.id, item.company_name || item.name)} className="text-emerald-600 hover:bg-emerald-50 rounded-full h-7 w-7 p-0" title={tr ? 'Arşivden çıkar' : 'Un-archive'} data-testid={`tenant-unarchive-btn-${item.id}`}>
                                            <ArchiveRestore className="h-3.5 w-3.5" />
                                          </Button>
                                        ) : (
                                          <Button size="sm" variant="ghost" onClick={() => handleArchiveTenant(item.id, item.company_name || item.name)} className="text-zinc-600 hover:bg-zinc-100 rounded-full h-7 w-7 p-0" title={tr ? 'Arşivle' : 'Archive'} data-testid={`tenant-archive-btn-${item.id}`}>
                                            <Archive className="h-3.5 w-3.5" />
                                          </Button>
                                        )}
                                        <Button size="sm" variant="ghost" onClick={() => handleDeleteClick(item.id, item.company_name || item.name || item.slug, 'tenants')} className="text-red-500 hover:bg-red-50 rounded-full h-7 w-7 p-0" title={tr ? 'Kalıcı Sil (Ultra Password)' : 'Permanent Delete (Ultra Password)'} data-testid={`tenant-delete-btn-${item.id}`}>
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </>
                            )}
                            {/* Orders row */}
                            {section === 'orders' && (
                              <>
                                <td className="px-6 py-4 text-xs text-gray-500 font-mono">{item.id?.slice(0, 8)}...</td>
                                <td className="px-6 py-4">
                                  <Badge className={`rounded-full text-xs ${orderStatusBadge(item.status)}`}>{item.status}</Badge>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700">
                                  {item.items?.length || 0} {tr ? 'ürün' : 'items'}
                                  {item.items?.[0] && <span className="text-gray-400 text-xs ml-1">({item.items[0].product_name})</span>}
                                </td>
                                <td className="px-6 py-4 text-sm font-semibold text-purple-700">${(item.total_amount || 0).toFixed(2)}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">
                                  {item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Products: show info card */}
          {section === 'products' && (
            <Card className="border-0 shadow-lg rounded-2xl mt-6 border-l-4 border-l-blue-500">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Package className="h-8 w-8 text-blue-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">
                      {tr ? 'Ürün Kataloğu Hakkında' : 'About Product Catalog'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {tr
                        ? 'Şu an 39 ürün (Stand, Mobilya, Technic) müşteri dashboard\'ında gösteriliyor. Ürünler mockAdmin.js dosyasından geliyor. Gerçek ürün yönetimi için backend entegrasyonu yapılabilir.'
                        : 'Currently 39 products (Stand, Furniture, Technic) are shown in the customer dashboard, coming from mockAdmin.js. Backend product management can be implemented.'}
                    </p>
                    <div className="flex gap-3 mt-4">
                      <div className="bg-sky-50 rounded-xl px-4 py-2 text-center">
                        <p className="text-2xl font-bold text-sky-700">12</p>
                        <p className="text-xs text-sky-500">Stands</p>
                      </div>
                      <div className="bg-purple-50 rounded-xl px-4 py-2 text-center">
                        <p className="text-2xl font-bold text-purple-700">15</p>
                        <p className="text-xs text-purple-600">Furniture</p>
                      </div>
                      <div className="bg-purple-50 rounded-xl px-4 py-2 text-center">
                        <p className="text-2xl font-bold text-purple-700">12</p>
                        <p className="text-xs text-purple-500">Technic</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          </>)}
        </div>
      </div>
      {/* Phase 86AT — Create Ultra Admin (system admin) modal. */}
      {createUserModal && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/55 p-4" data-testid="new-user-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-purple-700" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-lg">{tr ? 'Yeni Ultra Admin' : 'New Ultra Admin'}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{tr ? 'Sistem yöneticisi oluştur' : 'Create a system admin'}</p>
                </div>
              </div>
              <button onClick={() => setCreateUserModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold text-gray-600">{tr ? 'Ad Soyad' : 'Full Name'}</Label>
                <Input
                  data-testid="new-user-fullname"
                  autoFocus
                  value={createUserModal.full_name || ''}
                  onChange={e => setCreateUserModal(prev => ({ ...prev, full_name: e.target.value, error: '' }))}
                  placeholder={tr ? 'Ör. Ayşe Yılmaz' : 'e.g. Jane Doe'}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">{tr ? 'E-posta' : 'Email'}</Label>
                <Input
                  data-testid="new-user-email"
                  type="email"
                  value={createUserModal.email || ''}
                  onChange={e => setCreateUserModal(prev => ({ ...prev, email: e.target.value, error: '' }))}
                  placeholder="admin@example.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">{tr ? 'Şifre' : 'Password'}</Label>
                <Input
                  data-testid="new-user-password"
                  type="password"
                  value={createUserModal.password || ''}
                  onChange={e => setCreateUserModal(prev => ({ ...prev, password: e.target.value, error: '' }))}
                  placeholder={tr ? 'En az 8 karakter' : 'At least 8 characters'}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">{tr ? 'Şifre (Tekrar)' : 'Confirm Password'}</Label>
                <Input
                  data-testid="new-user-confirm"
                  type="password"
                  value={createUserModal.confirm || ''}
                  onChange={e => setCreateUserModal(prev => ({ ...prev, confirm: e.target.value, error: '' }))}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateUser(); }}
                  className="mt-1"
                />
              </div>
            </div>
            {createUserModal.error && (
              <p className="text-xs text-rose-600 mt-3" data-testid="new-user-error">{createUserModal.error}</p>
            )}
            <div className="flex gap-3 mt-5">
              <button
                data-testid="new-user-cancel"
                onClick={() => setCreateUserModal(null)}
                disabled={createUserModal.loading}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
              <button
                data-testid="new-user-confirm"
                onClick={handleCreateUser}
                disabled={createUserModal.loading}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50 bg-purple-700 hover:bg-purple-800">
                {createUserModal.loading ? (tr ? 'Oluşturuluyor…' : 'Creating…') : (tr ? 'Oluştur' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Phase 86AT — Manual Green Zone user creation modal (mirrors public form). */}
      {createGzModal && (() => {
        const countryOpts = getCountryOptions(language);
        const rules = checkPasswordRules(createGzModal.password || '');
        const allRulesOk = Object.values(rules).every(Boolean);
        const passwordsMismatch = !!(createGzModal.password && createGzModal.password2 && createGzModal.password !== createGzModal.password2);
        const fe = createGzModal.fieldErrors || {};
        const setF = (k, v) => setCreateGzModal(prev => {
          const nfe = { ...(prev.fieldErrors || {}) };
          delete nfe[k];
          return { ...prev, [k]: v, fieldErrors: nfe, error: '' };
        });
        const PassRule = ({ ok, label }) => (
          <div className={`flex items-center gap-1.5 text-[11px] ${ok ? 'text-emerald-600' : 'text-rose-500'}`}>
            {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}<span>{label}</span>
          </div>
        );
        const showPw  = !!createGzModal.showPw;
        const showPw2 = !!createGzModal.showPw2;
        return (
          <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/55 p-4" data-testid="new-gz-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-lg">{tr ? 'Yeni Green Zone Kullanıcısı' : 'New Green Zone User'}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{tr ? 'Manuel kayıt · Onay aşaması atlanır · Audit log\'a yazılır' : 'Manual registration · skips approval queue · audit-logged'}</p>
                  </div>
                </div>
                <button onClick={() => setCreateGzModal(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Ad Soyad' : 'Full Name'} *</Label>
                  <Input data-testid="gz-fullname" value={createGzModal.full_name} onChange={e => setF('full_name', e.target.value)} className="mt-1" />
                  {fe.full_name && <p className="text-[11px] text-rose-600 mt-1">{fe.full_name}</p>}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'E-posta' : 'Email'} *</Label>
                  <Input data-testid="gz-email" type="email" value={createGzModal.email} onChange={e => setF('email', e.target.value)} className="mt-1" />
                  {fe.email && <p className="text-[11px] text-rose-600 mt-1">{fe.email}</p>}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Firma / Organizasyon Adı' : 'Company / Organization'} *</Label>
                  <Input data-testid="gz-company" value={createGzModal.company} onChange={e => setF('company', e.target.value)} className="mt-1" />
                  {fe.company && <p className="text-[11px] text-rose-600 mt-1">{fe.company}</p>}
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Cep Telefonu' : 'Mobile'}</Label>
                  <div className="mt-1">
                    <PhoneInput
                      codeValue={createGzModal.phone_code}
                      numberValue={createGzModal.phone}
                      onCodeChange={(v) => setF('phone_code', v)}
                      onNumberChange={(v) => setF('phone', v)}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Ülke' : 'Country'}</Label>
                  <select
                    data-testid="gz-country"
                    value={createGzModal.country_code}
                    onChange={e => setF('country_code', e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-300">
                    <option value="">{tr ? '— Seçiniz —' : '— Select —'}</option>
                    {countryOpts.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Şehir' : 'City'}</Label>
                  <Input data-testid="gz-city" value={createGzModal.city} onChange={e => setF('city', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Ünvan' : 'Title'}</Label>
                  <Input data-testid="gz-title" value={createGzModal.title} onChange={e => setF('title', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Web Sitesi' : 'Website'}</Label>
                  <Input data-testid="gz-website" placeholder="https://" value={createGzModal.website} onChange={e => setF('website', e.target.value)} className="mt-1" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Adres' : 'Address'}</Label>
                  <Input data-testid="gz-address" value={createGzModal.address} onChange={e => setF('address', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Şifre' : 'Password'} *</Label>
                  <div className="relative mt-1">
                    <Input
                      data-testid="gz-password"
                      type={showPw ? 'text' : 'password'}
                      value={createGzModal.password}
                      onChange={e => setF('password', e.target.value)}
                      className={`pr-9 ${createGzModal.password && !allRulesOk ? 'border-rose-300 focus-visible:ring-rose-300' : ''}`}
                    />
                    <button
                      type="button"
                      data-testid="gz-password-toggle"
                      onClick={() => setCreateGzModal(prev => ({ ...prev, showPw: !prev.showPw }))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                      tabIndex={-1}
                      title={showPw ? (tr ? 'Gizle' : 'Hide') : (tr ? 'Göster' : 'Show')}>
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {fe.password && <p className="text-[11px] text-rose-600 mt-1">{fe.password}</p>}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Şifre (Tekrar)' : 'Confirm Password'} *</Label>
                  <div className="relative mt-1">
                    <Input
                      data-testid="gz-password2"
                      type={showPw2 ? 'text' : 'password'}
                      value={createGzModal.password2}
                      onChange={e => setF('password2', e.target.value)}
                      className={`pr-9 ${passwordsMismatch ? 'border-rose-300 focus-visible:ring-rose-300' : ''}`}
                    />
                    <button
                      type="button"
                      data-testid="gz-password2-toggle"
                      onClick={() => setCreateGzModal(prev => ({ ...prev, showPw2: !prev.showPw2 }))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                      tabIndex={-1}
                      title={showPw2 ? (tr ? 'Gizle' : 'Hide') : (tr ? 'Göster' : 'Show')}>
                      {showPw2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {fe.password2 && <p className="text-[11px] text-rose-600 mt-1">{fe.password2}</p>}
                  {!fe.password2 && passwordsMismatch && (
                    <p className="text-[11px] text-rose-600 mt-1" data-testid="gz-mismatch">
                      {tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.'}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 bg-gray-50 border border-gray-100 rounded-lg p-3">
                <PassRule ok={rules.length}    label={tr ? 'En az 8 karakter' : 'Min 8 chars'} />
                <PassRule ok={rules.uppercase} label={tr ? '1 büyük harf'     : '1 uppercase'} />
                <PassRule ok={rules.lowercase} label={tr ? '1 küçük harf'     : '1 lowercase'} />
                <PassRule ok={rules.number}    label={tr ? '1 rakam'          : '1 digit'} />
                <PassRule ok={rules.special}   label={tr ? '1 özel karakter'  : '1 special'} />
              </div>

              {createGzModal.error && (
                <p className="text-xs text-rose-600 mt-3" data-testid="gz-error">{createGzModal.error}</p>
              )}

              <div className="flex gap-3 mt-5">
                <button
                  data-testid="gz-cancel"
                  onClick={() => setCreateGzModal(null)}
                  disabled={createGzModal.loading}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
                  {tr ? 'Vazgeç' : 'Cancel'}
                </button>
                <button
                  data-testid="gz-confirm"
                  onClick={handleCreateGzUser}
                  disabled={createGzModal.loading}
                  className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50 bg-emerald-600 hover:bg-emerald-700">
                  {createGzModal.loading ? (tr ? 'Oluşturuluyor…' : 'Creating…') : (tr ? 'Oluştur' : 'Create')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Phase 86AZ.58 — Supplier manual creation modal. Mirrors /tedarikci-kayit
          fields exactly (category, sub, full_name, company, title, phone,
          country, city, email). Password is NOT collected here — supplier
          sets it via a 7-day emailed invitation link. */}
      {createSupplierModal && (() => {
        const f = createSupplierModal;
        const fe = f.fieldErrors || {};
        const countryOpts = getCountryOptions(language);
        const activeCat = supplierCategories.find(c => c.id === f.supplier_category_id);
        const activeSubs = activeCat?.subcategories || [];
        const setF = (k, v) => setCreateSupplierModal(prev => {
          const nfe = { ...(prev.fieldErrors || {}) };
          delete nfe[k];
          return { ...prev, [k]: v, fieldErrors: nfe, error: '' };
        });
        const onCategoryChange = (catId) => {
          const cat = supplierCategories.find(c => c.id === catId);
          setCreateSupplierModal(prev => {
            const nfe = { ...(prev.fieldErrors || {}) };
            delete nfe.supplier_category_id;
            delete nfe.supplier_sub_category_id;
            return {
              ...prev,
              supplier_category_id:       catId,
              supplier_category_name:     cat?.name || '',
              supplier_sub_category_id:   '',
              supplier_sub_category_name: '',
              fieldErrors: nfe, error: '',
            };
          });
        };
        const onSubChange = (subId) => {
          const sub = (activeCat?.subcategories || []).find(s => s.id === subId);
          setCreateSupplierModal(prev => {
            const nfe = { ...(prev.fieldErrors || {}) };
            delete nfe.supplier_sub_category_id;
            return {
              ...prev,
              supplier_sub_category_id:   subId,
              supplier_sub_category_name: sub?.name || '',
              fieldErrors: nfe, error: '',
            };
          });
        };
        return (
          <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/55 p-4" data-testid="new-supplier-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Package className="w-6 h-6 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-lg">{tr ? 'Yeni Tedarikçi Oluştur' : 'New Supplier'}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {tr
                        ? 'Manuel kayıt · Onay aşaması atlanır · Tedarikçiye 7 gün geçerli şifre belirleme bağlantısı e-postalanır'
                        : 'Manual registration · skips approval · supplier gets a 7-day password-setup email'}
                    </p>
                  </div>
                </div>
                <button data-testid="supplier-modal-close" onClick={() => setCreateSupplierModal(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Category */}
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Tedarikçi Kategorisi' : 'Supplier Category'} *</Label>
                  <div className="relative mt-1">
                    <select
                      data-testid="supplier-modal-category"
                      value={f.supplier_category_id}
                      onChange={e => onCategoryChange(e.target.value)}
                      className={`w-full pl-3 pr-8 py-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300 appearance-none text-gray-700 ${fe.supplier_category_id ? 'border-red-400' : 'border-gray-300'}`}
                    >
                      <option value="">{tr ? '-- Kategori Seçin --' : '-- Select Category --'}</option>
                      {supplierCategories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  {fe.supplier_category_id && <p className="text-[11px] text-rose-600 mt-1">{fe.supplier_category_id}</p>}
                </div>

                {activeSubs.length > 0 && (
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold text-gray-600">{tr ? 'Alt Kategori' : 'Sub-Category'} *</Label>
                    <div className="relative mt-1">
                      <select
                        data-testid="supplier-modal-subcategory"
                        value={f.supplier_sub_category_id}
                        onChange={e => onSubChange(e.target.value)}
                        className={`w-full pl-3 pr-8 py-2 border rounded-md text-sm bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-300 appearance-none text-gray-700 ${fe.supplier_sub_category_id ? 'border-red-400' : 'border-amber-200'}`}
                      >
                        <option value="">{tr ? '-- Alt Kategori --' : '-- Sub-Category --'}</option>
                        {activeSubs.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                    {fe.supplier_sub_category_id && <p className="text-[11px] text-rose-600 mt-1">{fe.supplier_sub_category_id}</p>}
                  </div>
                )}

                {/* Company */}
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Firma Adı' : 'Company Name'} *</Label>
                  <Input data-testid="supplier-modal-company" value={f.company} onChange={e => setF('company', e.target.value)} className="mt-1" />
                  {fe.company && <p className="text-[11px] text-rose-600 mt-1">{fe.company}</p>}
                </div>

                {/* Full name */}
                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Ad Soyad' : 'Full Name'} *</Label>
                  <Input data-testid="supplier-modal-fullname" value={f.full_name} onChange={e => setF('full_name', e.target.value)} className="mt-1" />
                  {fe.full_name && <p className="text-[11px] text-rose-600 mt-1">{fe.full_name}</p>}
                </div>

                {/* Title */}
                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Görev / Unvan' : 'Title'}</Label>
                  <Input data-testid="supplier-modal-title" value={f.title} onChange={e => setF('title', e.target.value)} className="mt-1" />
                </div>

                {/* Email */}
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'E-posta' : 'Email'} *</Label>
                  <Input data-testid="supplier-modal-email" type="email" value={f.email} onChange={e => setF('email', e.target.value)} className="mt-1" />
                  {fe.email && <p className="text-[11px] text-rose-600 mt-1">{fe.email}</p>}
                </div>

                {/* Phone */}
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Telefon' : 'Phone'}</Label>
                  <div className="mt-1">
                    <PhoneInput
                      codeValue={f.phone_code}
                      numberValue={f.phone}
                      onCodeChange={(v) => setF('phone_code', v)}
                      onNumberChange={(v) => setF('phone', v)}
                    />
                  </div>
                </div>

                {/* Country */}
                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Ülke' : 'Country'}</Label>
                  <select
                    data-testid="supplier-modal-country"
                    value={f.country_code}
                    onChange={e => setF('country_code', e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300">
                    <option value="">{tr ? '— Seçiniz —' : '— Select —'}</option>
                    {countryOpts.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>

                {/* City */}
                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Şehir' : 'City'}</Label>
                  <Input data-testid="supplier-modal-city" value={f.city} onChange={e => setF('city', e.target.value)} className="mt-1" />
                </div>
              </div>

              <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                <Mail className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  {tr
                    ? 'Tedarikçiye e-posta ile 7 gün geçerli güvenli bir şifre belirleme bağlantısı gönderilecektir. Şifresini belirler belirlemez tedarikçi paneline otomatik giriş yapacaktır.'
                    : 'A 7-day password-setup link will be emailed to the supplier. They will be auto-logged into the supplier dashboard right after setting it.'}
                </p>
              </div>

              {createSupplierModal.error && (
                <p className="text-xs text-rose-600 mt-3" data-testid="supplier-modal-error">{createSupplierModal.error}</p>
              )}

              <div className="flex gap-3 mt-5">
                <button
                  data-testid="supplier-modal-cancel"
                  onClick={() => setCreateSupplierModal(null)}
                  disabled={createSupplierModal.loading}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
                  {tr ? 'Vazgeç' : 'Cancel'}
                </button>
                <button
                  data-testid="supplier-modal-confirm"
                  onClick={handleCreateSupplierUser}
                  disabled={createSupplierModal.loading}
                  className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50 bg-amber-600 hover:bg-amber-700">
                  {createSupplierModal.loading
                    ? (tr ? 'Oluşturuluyor…' : 'Creating…')
                    : (tr ? 'Oluştur & Davet Gönder' : 'Create & Send Invite')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Phase 86AZ.59 — Contractor manual creation modal. Mirrors /yuklenci-kayit
          fields (category, company, full_name, title, email, phone, country,
          city, website, about_us). Password is NOT collected — contractor sets
          it via a 7-day emailed invitation link. */}
      {createContractorModal && (() => {
        const f = createContractorModal;
        const fe = f.fieldErrors || {};
        const countryOpts = getCountryOptions(language);
        const setF = (k, v) => setCreateContractorModal(prev => {
          const nfe = { ...(prev.fieldErrors || {}) };
          delete nfe[k];
          return { ...prev, [k]: v, fieldErrors: nfe, error: '' };
        });
        const onCategoryChange = (catId) => {
          const cat = contractorCategories.find(c => c.id === catId);
          setCreateContractorModal(prev => {
            const nfe = { ...(prev.fieldErrors || {}) };
            delete nfe.contractor_category_id;
            return {
              ...prev,
              contractor_category_id:   catId,
              contractor_category_name: cat?.name || '',
              fieldErrors: nfe, error: '',
            };
          });
        };
        return (
          <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/55 p-4" data-testid="new-contractor-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Plus className="w-6 h-6 text-red-700" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-lg">{tr ? 'Yeni Müteahhit Oluştur' : 'New Contractor'}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {tr
                        ? 'Manuel kayıt · Onay aşaması atlanır · Müteahhite 7 gün geçerli şifre belirleme bağlantısı e-postalanır'
                        : 'Manual registration · skips approval · contractor gets a 7-day password-setup email'}
                    </p>
                  </div>
                </div>
                <button data-testid="contractor-modal-close" onClick={() => setCreateContractorModal(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Müteahhit Kategorisi' : 'Contractor Category'} *</Label>
                  <div className="relative mt-1">
                    <select
                      data-testid="contractor-modal-category"
                      value={f.contractor_category_id}
                      onChange={e => onCategoryChange(e.target.value)}
                      className={`w-full pl-3 pr-8 py-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-300 appearance-none text-gray-700 ${fe.contractor_category_id ? 'border-red-400' : 'border-gray-300'}`}
                    >
                      <option value="">{tr ? '-- Kategori Seçin --' : '-- Select Category --'}</option>
                      {contractorCategories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  {fe.contractor_category_id && <p className="text-[11px] text-rose-600 mt-1">{fe.contractor_category_id}</p>}
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Firma Adı' : 'Company Name'} *</Label>
                  <Input data-testid="contractor-modal-company" value={f.company} onChange={e => setF('company', e.target.value)} className="mt-1" />
                  {fe.company && <p className="text-[11px] text-rose-600 mt-1">{fe.company}</p>}
                </div>

                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Ad Soyad' : 'Full Name'} *</Label>
                  <Input data-testid="contractor-modal-fullname" value={f.full_name} onChange={e => setF('full_name', e.target.value)} className="mt-1" />
                  {fe.full_name && <p className="text-[11px] text-rose-600 mt-1">{fe.full_name}</p>}
                </div>

                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Görev / Unvan' : 'Title'}</Label>
                  <Input data-testid="contractor-modal-title" value={f.title} onChange={e => setF('title', e.target.value)} className="mt-1" />
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'E-posta' : 'Email'} *</Label>
                  <Input data-testid="contractor-modal-email" type="email" value={f.email} onChange={e => setF('email', e.target.value)} className="mt-1" />
                  {fe.email && <p className="text-[11px] text-rose-600 mt-1">{fe.email}</p>}
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Telefon' : 'Phone'}</Label>
                  <div className="mt-1">
                    <PhoneInput
                      codeValue={f.phone_code}
                      numberValue={f.phone}
                      onCodeChange={(v) => setF('phone_code', v)}
                      onNumberChange={(v) => setF('phone', v)}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Ülke' : 'Country'}</Label>
                  <select
                    data-testid="contractor-modal-country"
                    value={f.country_code}
                    onChange={e => setF('country_code', e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-300">
                    <option value="">{tr ? '— Seçiniz —' : '— Select —'}</option>
                    {countryOpts.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Şehir' : 'City'}</Label>
                  <Input data-testid="contractor-modal-city" value={f.city} onChange={e => setF('city', e.target.value)} className="mt-1" />
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Web Sitesi' : 'Website'}</Label>
                  <Input data-testid="contractor-modal-website" placeholder="https://" value={f.website} onChange={e => setF('website', e.target.value)} className="mt-1" />
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">{tr ? 'Hakkımızda' : 'About Us'}</Label>
                  <textarea
                    data-testid="contractor-modal-about"
                    value={f.about_us}
                    onChange={e => setF('about_us', e.target.value)}
                    rows={3}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
                  />
                </div>
              </div>

              <div className="mt-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                <Mail className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-800 leading-relaxed">
                  {tr
                    ? 'Müteahhite e-posta ile 7 gün geçerli güvenli bir şifre belirleme bağlantısı gönderilecektir. Şifresini belirler belirlemez müteahhit paneline otomatik giriş yapacaktır.'
                    : 'A 7-day password-setup link will be emailed to the contractor. They will be auto-logged into the contractor dashboard right after setting it.'}
                </p>
              </div>

              {createContractorModal.error && (
                <p className="text-xs text-rose-600 mt-3" data-testid="contractor-modal-error">{createContractorModal.error}</p>
              )}

              <div className="flex gap-3 mt-5">
                <button
                  data-testid="contractor-modal-cancel"
                  onClick={() => setCreateContractorModal(null)}
                  disabled={createContractorModal.loading}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
                  {tr ? 'Vazgeç' : 'Cancel'}
                </button>
                <button
                  data-testid="contractor-modal-confirm"
                  onClick={handleCreateContractorUser}
                  disabled={createContractorModal.loading}
                  className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50 bg-red-600 hover:bg-red-700">
                  {createContractorModal.loading
                    ? (tr ? 'Oluşturuluyor…' : 'Creating…')
                    : (tr ? 'Oluştur & Davet Gönder' : 'Create & Send Invite')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Phase 86AY.1/2 — Invitation preview + send modal (also handles re-invite/reminder). */}
      {inviteModal && (() => {
        const isReminder = !!inviteModal.isReminder;
        const theme = isReminder
          ? { bg:'bg-amber-100', txt:'text-amber-700', btn:'bg-amber-600 hover:bg-amber-700', icon: Bell,
              title:    tr ? 'Yeniden Davet (Hatırlatma)'      : 'Re-invite (Reminder)',
              subtitle: tr ? 'Yeni 6-haneli kod oluşturulup yollanır · Audit log\'a yazılır'
                            : 'A new 6-digit code is generated · audit-logged',
              cta:      tr ? 'Hatırlat' : 'Remind' }
          : { bg:'bg-emerald-100', txt:'text-emerald-700', btn:'bg-emerald-600 hover:bg-emerald-700', icon: Send,
              title:    tr ? 'Davet Maili Önizleme' : 'Invitation Preview',
              subtitle: tr ? 'Onaylamadan mail gönderilmez · Audit log\'a yazılır'
                            : 'No mail leaves until you confirm · audit-logged',
              cta:      tr ? 'Gönder' : 'Send' };
        const Icon = theme.icon;
        return (
        <div className="fixed inset-0 z-[145] flex items-center justify-center bg-black/55 p-4" data-testid="invite-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full ${theme.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-6 h-6 ${theme.txt}`} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-lg">{theme.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{theme.subtitle}</p>
                </div>
              </div>
              <button onClick={() => setInviteModal(null)} disabled={inviteModal.sending} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
                <X className="w-5 h-5" />
              </button>
            </div>

            {inviteModal.loading && (
              <div className="py-10 flex items-center justify-center text-gray-400">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                {tr ? 'Önizleme yükleniyor…' : 'Loading preview…'}
              </div>
            )}

            {!inviteModal.loading && inviteModal.preview && (
              <>
                <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-3 text-xs space-y-1">
                  <div><span className="text-gray-400">{tr ? 'Alıcı:' : 'To:'}</span> <strong className="text-gray-800">{inviteModal.preview.to}</strong></div>
                  <div><span className="text-gray-400">{tr ? 'Konu:' : 'Subject:'}</span> <strong className="text-gray-800">{inviteModal.preview.subject}</strong></div>
                  <div><span className="text-gray-400">{tr ? 'Stand:' : 'Stand:'}</span> <strong className="text-emerald-700">{inviteModal.preview.stand_no}</strong></div>
                  {isReminder && (
                    <div className="pt-1 border-t border-gray-200 mt-1">
                      <span className="text-amber-700 font-semibold">
                        {tr ? '⚠ Bu kullanıcıya daha önce davet gönderildi — bu bir hatırlatma maili olacak.' : '⚠ This user was already invited — this will be a reminder mail.'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50 max-h-[50vh] overflow-y-auto p-4">
                  <div dangerouslySetInnerHTML={{ __html: inviteModal.preview.html }} />
                </div>
                <p className="text-[11px] text-gray-400 mt-2">
                  {tr ? 'Önizlemede gösterilen kod placeholder. Gönder\'e bastığınızda yeni 6-haneli bir kod oluşturulup yollanır.' : 'The code shown is a placeholder. A fresh 6-digit code is generated when you click Send.'}
                </p>
              </>
            )}

            {inviteModal.error && (
              <p className="text-xs text-rose-600 mt-3" data-testid="invite-error">{inviteModal.error}</p>
            )}

            <div className="flex gap-3 mt-5">
              <button
                data-testid="invite-cancel"
                onClick={() => setInviteModal(null)}
                disabled={inviteModal.sending}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
              <button
                data-testid="invite-send"
                onClick={handleSendInvite}
                disabled={inviteModal.sending || inviteModal.loading || !inviteModal.preview}
                className={`flex-1 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50 ${theme.btn} inline-flex items-center justify-center gap-2`}>
                <Icon className="w-4 h-4" />
                {inviteModal.sending ? (tr ? 'Gönderiliyor…' : 'Sending…') : theme.cta}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default UltraAdminSection;
